/**
 * App.js — root component and application shell.
 *
 * Responsibilities:
 *  - Own the single global `data` object
 *    ({ items, transformations, history, recipes, managedLists, settings, categories, cars,
 *    siblingLinks, contacts, businesses, categoryVisits, _normVersion }).
 *  - Persist it to localStorage via a debounced save, with a beforeunload guard.
 *  - Provide an immutable update(fn) updater used by every handler.
 *  - Route between the six pages (Daftar Barang / Transformasi / Resep / Mobil / Kontak /
 *    Riwayat Transformasi) plus the off-sidebar Import Wizard (catalog Excel import, reached
 *    only via a Daftar Barang header button), and host the modals.
 *
 * localStorage only this session — no backend, no auth.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import DaftarBarang from "./pages/DaftarBarang";
import Transformasi from "./pages/Transformasi";
import RecipeManager from "./pages/RecipeManager";
import MobilPage, { deriveCars } from "./pages/MobilPage";
import KontakPage from "./pages/KontakPage";
import BisnisPage from "./pages/BisnisPage";
import Penjualan from "./pages/Penjualan";
import Pembelian from "./pages/Pembelian";
import PiutangUtang from "./pages/PiutangUtang";
import RiwayatTransformasi from "./pages/RiwayatTransformasi";
import ImportWizard from "./pages/ImportWizard";
import ImportKontakWizard from "./pages/ImportKontakWizard";
import ItemFormModal from "./components/ItemFormModal";
import PenyesuaianModal from "./components/PenyesuaianModal";
import DetailBarangModal from "./components/DetailBarangModal";
import DeleteConfirmModal from "./components/DeleteConfirmModal";
import RecipeDetailModal from "./components/RecipeDetailModal";
import RecipeEditModal from "./components/RecipeEditModal";
import ConfirmModal from "./components/ConfirmModal";
import MobilDetailModal from "./components/MobilDetailModal";
import KelolaKategoriModal from "./components/KelolaKategoriModal";
import KategoriDetailModal from "./components/KategoriDetailModal";
import PengaturanModal from "./components/PengaturanModal";
import {
  DaftarBarangIcon, PenjualanIcon, PembelianIcon, PiutangUtangIcon, TransformasiIcon,
  ResepIcon, MobilIcon, KontakIcon, PengaturanIcon, CollapseIcon, PengeluaranIcon,
} from "./components/Icons";

import { loadData, saveData, NORM_VERSION, prepareLoadedData } from "./utils/storage";
import { createSyncEngine, isStoppedState, sameValue } from "./utils/remoteSync";
import { loadDeviceLabel, saveDeviceLabel } from "./utils/devicePrefs";
import DeviceLabelModal from "./components/DeviceLabelModal";
import SyncNotice from "./components/SyncNotice";
import { loadSidebarCollapsed, saveSidebarCollapsed } from "./utils/sidebarPrefs";
import { parseFitment } from "./utils/fitment";
import { generateId, fmtQty, fmtDate, fmtDateShort, maxCodeNumbersByPrefix } from "./utils/idGenerators";
import { diffTxnLines } from "./utils/lineDiff";
import { applyTransformation, bucketLabel } from "./utils/transformations";
import { creationEntries, transactionEntries, transactionEditEntries } from "./utils/history";
import { resolveItemCreate, resolveCategoryForCreate } from "./utils/categoryResolve";
import { mergeCar, renameCar, deleteCar } from "./utils/cars";
import { renameCategory, deleteCategory } from "./utils/categories";
import { siblingsOf, linkSiblings, unlinkSiblings, removeItemLinks } from "./utils/siblings";
import {
  addContact, updateContact, deleteContact, validateContactFields, displayContact,
  findInternalContact, defaultAddressText, ensureInternalContacts, removeInternalContactForBranch,
} from "./utils/contacts";
import { addBusiness, businessExists } from "./utils/businesses";
import { addSalesperson, updateSalesperson, deleteSalesperson, moveSalespersonBranch, resolveSalespersonName, resolveSalespersonCode } from "./utils/salespeople";
import { addBranch, updateBranch, deleteBranch, resolveBranchName } from "./utils/branches";
import { addPerson, updatePerson, deletePerson } from "./utils/persons";
import { mintPusatNumber, mintCabangNumber } from "./utils/invoiceNumbers";
import { applyTxnStockDelta } from "./utils/txnStock";
import { txnTotal, txnSisa, validateTxnTotal } from "./utils/txnStatus";
import { validatePayments, validatePaymentSet, paymentMethodText } from "./utils/payments";
import { validateCostItems, costItemTypeLabel } from "./utils/costItems";
import { addExpense, updateExpense, deleteExpense, validateExpense, expensesForTxn, expenseLabel, syncLinkedExpenses, isStandaloneExpense } from "./utils/expenses";
import { ExpensesContext } from "./components/ExpensesContext";
import Pengeluaran from "./pages/Pengeluaran";
import { paymentSplit, tagihanPotonganOwed, barangOwed, barangPaid, tagihanPotonganPaid, validateSplitSum, checkBucketOverAllocation, bucketOverAllocationMessage, validateBucketCompleteness } from "./utils/paymentSplit";
import { SEED_CODES, wipedData } from "./utils/importWipe";

// Pages that are themselves a form in progress: other people's changes are held back while one
// is open (multi-device sync, step 5b — see the check-for-updates effect inside App).
const UPDATE_HOLD_PAGES = new Set(["transformasi", "impor", "impor-kontak"]);

const NAV = [
  { key: "daftar", label: "Daftar Barang" },
  { key: "penjualan", label: "Penjualan" },
  { key: "pembelian", label: "Pembelian" },
  { key: "piutangutang", label: "Piutang & Utang" },
  { key: "pengeluaran", label: "Pengeluaran" },
  { key: "transformasi", label: "Transformasi" },
  { key: "resep", label: "Resep" },
  { key: "mobil", label: "Mobil" },
  { key: "kontak", label: "Kontak" },
];

const NAV_ICONS = {
  daftar: DaftarBarangIcon,
  penjualan: PenjualanIcon,
  pembelian: PembelianIcon,
  piutangutang: PiutangUtangIcon,
  pengeluaran: PengeluaranIcon,
  transformasi: TransformasiIcon,
  resep: ResepIcon,
  mobil: MobilIcon,
  kontak: KontakIcon,
};

/** Snapshot each recipe line's current item name (cached for when the item is later deleted). */
const stampNames = (items, lines = []) => {
  const nameByCode = new Map(items.map((it) => [it.internalCode, it.name]));
  return lines.map((l) => {
    const nm = nameByCode.get(l.internalCode);
    return nm != null ? { ...l, name: nm } : { ...l };
  });
};

/** Resolve the app-maintained internal contact a TRANSFER is billed against (Contact model
 *  redesign — replaces the old single shared "Cabang (internal)"/"Pusat (internal)" contact with
 *  one internal contact per real location). CABANG_TO_PUSAT always resolves the single
 *  branch-independent CABANG-list "Pusat" stand-in; PUSAT_TO_CABANG resolves the PUSAT-list
 *  stand-in for the SPECIFIC destination branch, given by `branchId`. Runs
 *  `ensureInternalContacts` first as a safety net (the real provisioning happens when a branch
 *  is added — see `handleAddBranch` below — this only guarantees the lookup below can never miss
 *  a genuinely-existing branch). `contact` comes back `null` when `branchId` doesn't resolve to a
 *  real branch (e.g. PUSAT_TO_CABANG with no/invalid destination picked yet) — the caller's own
 *  existing branch-validation abort still fires in that case, so no bad contactId ever persists.
 *  **Consequence for any per-contact view** (e.g. KontakPage's Riwayat Transaksi, batch 2c):
 *  filtering transactions by `contactId` is scoped per-branch for a PUSAT-list internal contact,
 *  but the single CABANG-list "Pusat" stand-in mixes every branch's CABANG_TO_PUSAT transfers
 *  together — there is only one such record, so that's the honest meaning of "its transactions,"
 *  not a filtering bug. **Answered, not just documented, as of batch 2c-fix**: every transaction
 *  row now carries a live-resolved Lokasi column (the real branch name, or "Pusat"), and
 *  KontakPage additionally offers a branch filter — shown only when a selected internal
 *  contact's own transactions actually span more than one branch (true only for this one
 *  "Pusat" stand-in) — so "what did MMC send us this month" is one selection, not a scan. See
 *  `src/pages/CLAUDE.md`'s own KontakPage entry for the full contract. */
const resolveTransferContact = (d, direction, branchId) => {
  const nd = ensureInternalContacts(d);
  const contact = direction === "PUSAT_TO_CABANG"
    ? findInternalContact(nd.contacts, "PUSAT", branchId ?? null)
    : findInternalContact(nd.contacts, "CABANG", null);
  return { data: nd, contact };
};

/**
 * Two ways to run (multi-device sync, step 4):
 *   - local (no props — tests, `npm start`, any build without Supabase settings): data comes
 *     from / goes to localStorage via storage.js, exactly as before;
 *   - shared online books: `RemoteBooksGate` loads the data first and passes
 *     `initialData` + `remote = { store, version }`; every save then goes through
 *     utils/remoteSync.js's version-checked queue instead of localStorage.
 */
export default function App({ initialData = null, remote = null } = {}) {
  const [data, setData] = useState(() => initialData || loadData());
  const dataRef = useRef(data);

  const [page, setPage] = useState("daftar");           // "daftar" | "transformasi" | "resep" | "mobil" | "kontak" | "riwayat"
  // One-shot target for KontakPage's own initial tab/selection (Bisnis page work) — set only when
  // a Bisnis-page member row is clicked, read once by KontakPage's own lazy initial state on the
  // fresh mount that follows (page === "kontak" is a conditional render block, so KontakPage
  // always remounts on the way in). Reset to null in `goNav` below so an ordinary sidebar click
  // into Kontak — including clicking "Kontak" itself — never re-applies a stale jump.
  const [kontakJumpTo, setKontakJumpTo] = useState(null); // {contactId, list} | null
  const [selectedCode, setSelectedCode] = useState(null); // internalCode shown in Detail
  // Section E3: the transformation Riwayat Transformasi opens on (expanded, in range) when it was
  // reached from an item's riwayat line; null when reached from Transformasi's own button.
  const [riwayatFocusId, setRiwayatFocusId] = useState(null);
  const [transformTarget, setTransformTarget] = useState(null); // prefill code for Transformasi

  const [itemModal, setItemModal] = useState(null);     // { initial } | { create:true } | null
  const [adjustItem, setAdjustItem] = useState(null);   // item being adjusted | null
  const [deleteTarget, setDeleteTarget] = useState(null); // item pending delete-confirm | null
  const [selectedRecipeId, setSelectedRecipeId] = useState(null); // recipe shown in RecipeDetailModal | null
  const [deleteRecipeTarget, setDeleteRecipeTarget] = useState(null); // recipe pending delete-confirm | null
  const [editRecipeTarget, setEditRecipeTarget] = useState(null); // recipe being edited | null
  const [selectedCarKey, setSelectedCarKey] = useState(null);
  const [kelolaKategori, setKelolaKategori] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [importOkMsg, setImportOkMsg] = useState(""); // success banner shown on Daftar after a commit
  const [pengaturanOpen, setPengaturanOpen] = useState(false); // T2-settings — sidebar entry, not a nav page
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => loadSidebarCollapsed());

  // "ok" | "pending" | "err" — plus, on the shared online books only: "offline" (retrying),
  // and the three stopped states "conflict" | "outdated" | "rejected" (see utils/remoteSync.js).
  const [saveState, setSaveState] = useState("ok");
  const [syncProblem, setSyncProblem] = useState(null); // the stopped status object, if any
  const saveTimer = useRef();
  const allowUnloadRef = useRef(false); // set right before a deliberate "Muat ulang halaman"

  // This computer's name, sent with every shared save (utils/devicePrefs.js). Read through a
  // ref so a rename in Pengaturan applies to the very next save.
  const [device, setDevice] = useState(() => loadDeviceLabel());
  const deviceLabelRef = useRef(device.label);
  useEffect(() => { deviceLabelRef.current = device.label; }, [device.label]);
  const [devicePromptDismissed, setDevicePromptDismissed] = useState(false);

  // What the last merge with someone else's changes reported (utils/remoteSync.js notices).
  const [syncNotice, setSyncNotice] = useState(null);

  // The shared-books sync engine — created once, only when running on the online copy. It owns
  // the books from then on: every change goes through `saveQueue.apply`, and a merge with
  // someone else's newer books replaces what's shown via onData.
  const [saveQueue] = useState(() => (remote ? createSyncEngine({
    store: remote.store,
    data: initialData,
    version: remote.version,
    normVersion: NORM_VERSION,
    prepare: (d) => prepareLoadedData(d).migrated,
    getSavedBy: () => deviceLabelRef.current || null,
    onData: (nd) => { dataRef.current = nd; setData(nd); },
    onStatus: (s) => {
      setSaveState(s.state === "error" ? "err" : s.state);
      setSyncProblem(isStoppedState(s.state) ? s : null);
    },
    onNotice: (n) => setSyncNotice(n),
  }) : null));
  useEffect(() => () => { if (saveQueue) saveQueue.dispose(); }, [saveQueue]);

  // ── Seeing other people's changes (multi-device sync, step 5b) ──────────────
  // On the shared books: check the version on window focus / tab becoming visible, and every
  // 30 s while visible (never while minimised or in a background tab). The books are only
  // downloaded when the version changed — see remoteSync.js's checkForUpdates.
  // The screen never changes under a form (decision 2026-10-08): not while any modal window or
  // print preview is open, nor on the three pages that ARE forms in progress — Transformasi
  // (composing a run), and the two Excel import wizards (built from a snapshot of the whole
  // books, which swapping in newer books underneath would let them overwrite). A newer version
  // seen meanwhile is taken on within a second of the form closing.
  const pageRef = useRef(page);
  useEffect(() => { pageRef.current = page; }, [page]);
  const canTakeUpdates = useCallback(
    () => !document.querySelector(".modal-overlay, .print-doc-overlay") && !UPDATE_HOLD_PAGES.has(pageRef.current),
    []
  );
  useEffect(() => {
    if (!saveQueue) return undefined;
    const check = () => {
      if (document.visibilityState === "hidden") return;
      saveQueue.checkForUpdates(canTakeUpdates);
    };
    const poll = setInterval(check, (remote && remote.pollMs) || 30000);
    // A held-back update: a cheap local check each second, the network only once it can apply.
    const waiter = setInterval(() => { if (saveQueue.hasWaitingUpdate() && canTakeUpdates()) check(); }, 1000);
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(poll);
      clearInterval(waiter);
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [saveQueue, canTakeUpdates, remote]);

  // Keep dataRef mirroring the latest committed state for update()'s reads.
  useEffect(() => { dataRef.current = data; }, [data]);

  // ── Debounced persistence (localStorage mode) ───────────────────────────────
  const persist = useCallback((nd) => {
    setSaveState("pending");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      setSaveState(saveData(nd) ? "ok" : "err");
    }, 500);
  }, []);

  /** Immutable updater: compute next data from latest committed state, set + persist.
   *  `meta` (optional) only matters on the shared online books: it tells the sync engine how to
   *  re-apply this change if someone else saved first — see utils/remoteSync.js's header. */
  const update = useCallback((fn, meta) => {
    if (saveQueue) { saveQueue.apply(fn, meta); return; }
    const nd = fn(dataRef.current);
    dataRef.current = nd;
    setData(nd);
    persist(nd);
  }, [persist, saveQueue]);

  // Warn if the browser closes while a debounced save is still pending.
  useEffect(() => {
    const handler = (e) => {
      if (allowUnloadRef.current) return;
      if (saveState !== "ok") { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [saveState]);

  // ── Derived lookups ──────────────────────────────────────────────────────────
  const selectedItem = useMemo(
    () => data.items.find((it) => it.internalCode === selectedCode) || null,
    [data.items, selectedCode]
  );
  const selectedRecipe = data.recipes.find((r) => r.id === selectedRecipeId) || null;
  const selectedCar = selectedCarKey
    ? deriveCars(data.items).find((c) => c.key === (selectedCarKey || "").toLowerCase()) || null
    : null;

  // Older write paths (item/category/car/contact/salesperson/branch CRUD) validate entirely
  // inside their underlying utils/*.js function via a same-reference no-op convention — unlike
  // the transaction write paths below, none of them previously surfaced a rejection anywhere.
  // Wraps such a call so a same-reference no-op logs a console.warn naming the check, matching
  // the transaction paths' "functionName: description" message style.
  const logNoOp = (label, message, before, after) => {
    if (after === before) console.warn(`${label}: ${message}`);
    return after;
  };

  // ── Multi-device sync (step 5a): naming changes and the records they edit ─────────────────
  // Passed as update()'s second argument. Only used on the shared online books, where a change
  // may have to be re-applied on top of someone else's newer data (utils/remoteSync.js):
  //   label   — names the change in a message if it can't be re-applied;
  //   touches — the record(s) an EDIT changes. If someone else changed one of them meanwhile,
  //             the edit is not re-applied (it was based on what the form showed) and the user
  //             is told — decision 2026-10-08, "stop and tell", never silently overwrite.
  const findTxn = (d, id) => (d.transactions || []).find((t) => t.id === id) || null;
  const txnName = (t) => (t ? t.invoiceNumber || t.supplierInvoiceNo || "tanpa nomor" : "yang sudah dihapus");
  const recordTouch = (what, get) => ({ what, get });
  const touchTxn = (id) => recordTouch("Transaksi ini", (d) => findTxn(d, id));
  const touchContact = (id) => recordTouch("Kontak ini", (d) => (d.contacts || []).find((c) => c.id === id) || null);
  const touchItem = (code) => recordTouch("Barang ini", (d) => (d.items || []).find((it) => it.internalCode === code) || null);
  // An item's details WITHOUT its stock — editing a name must not clash with a sale of it.
  const touchItemDetails = (code) => recordTouch("Data barang ini", (d) => {
    const it = (d.items || []).find((x) => x.internalCode === code);
    if (!it) return null;
    const { stokPusat, stokCabang, ...details } = it;
    return { ...details, siblings: [...siblingsOf(d, code)].sort() };
  });
  const touchRole = (id) => recordTouch("Data sales ini", (d) => (d.salespeople || []).find((s) => s.id === id) || null);
  const touchPersonOfRole = (id) => recordTouch("Data sales ini", (d) => {
    const role = (d.salespeople || []).find((s) => s.id === id);
    return role ? (d.persons || []).find((p) => p.id === role.personId) || null : null;
  });
  const touchPerson = (id) => recordTouch("Data orang ini", (d) => (d.persons || []).find((p) => p.id === id) || null);
  const touchBranch = (id) => recordTouch("Cabang ini", (d) => (d.branches || []).find((b) => b.id === id) || null);
  const touchRecipe = (id) => recordTouch("Resep ini", (d) => (d.recipes || []).find((r) => r.id === id) || null);
  const touchExpense = (id) => recordTouch("Pengeluaran ini", (d) => (d.expenses || []).find((e) => e.id === id) || null);
  const touchSetting = (key, what) => recordTouch(what, (d) => (d.settings || {})[key]);
  const contactLabel = (d, id) => {
    const c = (d.contacts || []).find((x) => x.id === id);
    return c ? displayContact(c, d.branches) : "yang sudah dihapus";
  };

  const createCategory = (name, code) =>
    update((d) => {
      const tidyName = (s) => String(s || "").trim().replace(/\s+/g, " ");
      const nm = tidyName(name);
      const cd = (code || "").trim().toUpperCase();
      if (!nm || !cd) {
        console.warn("createCategory: nama atau kode kosong");
        return d;
      }
      const cats = d.categories || [];
      if (cats.some((c) => tidyName(c.name).toLowerCase() === nm.toLowerCase())) {
        console.warn("createCategory: nama kategori sudah dipakai");
        return d;
      }
      if (cats.some((c) => String(c.code).toUpperCase() === cd)) {
        console.warn("createCategory: kode kategori sudah dipakai");
        return d;
      }
      return { ...d, categories: [...cats, { name: nm, code: cd }] };
    }, { label: `Tambah kategori "${String(name || "").trim()}"` });

  const createCar = (name) =>
    update((d) => {
      const nm = (name || "").trim();
      if (!nm) {
        console.warn("createCar: nama kosong");
        return d;
      }
      const cars = d.cars || [];
      if (cars.some((c) => String(c.name).trim().toUpperCase() === nm.toUpperCase())) {
        console.warn("createCar: nama mobil sudah dipakai");
        return d;
      }
      return { ...d, cars: [...cars, { name: nm }] };
    }, { label: `Tambah mobil "${(name || "").trim()}"` });

  // ContactPicker's inline "+ Tambah kontak baru" only ever collects a name — it has no `list`
  // or `personIds` context yet (list-scoping the picker and adding a person picker there are
  // later-stage UI work, out of scope for the Contact model redesign's stage 1). Defaults to the
  // CABANG list as a placeholder until that lands. Contact model redesign fix: this used to
  // silently ATTACH to a pre-existing same-named contact on a collision, with no sign anything
  // unusual happened (addContact's own same-reference no-op, then a name-match lookup that found
  // the wrong record). Now it validates first and returns `{id, error}` — a validation failure
  // (a name collision, or `personIds` now required on a normal contact, which this path can
  // never supply) surfaces a real reason instead of ever returning an unrelated existing id.
  const createContact = (name) => {
    const nm = (name || "").trim();
    let result = { id: null, error: null };
    update((d) => {
      const fields = { name: nm, list: "CABANG" };
      const check = validateContactFields(d, fields);
      if (!check.ok) {
        result = { id: null, error: check.error };
        return d;
      }
      const nd = addContact(d, fields);
      const match = (nd.contacts || []).find(
        (c) => c.list === "CABANG" && c.name.toLowerCase() === nm.toLowerCase()
      );
      result = match ? { id: match.id, error: null } : { id: null, error: "kontak tidak dibuat" };
      return nd;
    }, { label: `Tambah kontak "${nm}"` });
    return result;
  };

  // Contacts/salespeople link-up, Part 1: the FULL-FORM contact create — backs ContactPicker's
  // own nested ContactFormModal (opened when a host passes `createList`, e.g.
  // TransactionFormModal's Pembeli/Penjual picker). Unlike `createContact` above (name-only,
  // always CABANG, used by pickers with no genuine location anchor), this accepts the same real
  // field shape `handleAddContact`/`ContactFormModal` already use — `list` comes from the
  // caller's own transaction anchor, `personIds` from a deliberate pick in the nested form — and
  // returns the new id (which `handleAddContact` doesn't) so the picker can select it
  // immediately. Same validate-then-addContact-then-find-by-name-and-list pattern as
  // `createContact`, just against the real payload instead of a forced one.
  //
  // Bug fix (manual-testing report): also returns the full new `contact` record, synchronously,
  // in this same result — `update()`'s own state write only lands on the NEXT render, so a
  // caller (`ContactPicker`'s `submitCreateForm`) that immediately needs the real contact object
  // (to resolve a role for it right away) can't get one by reading its own `contacts` prop yet;
  // that prop is still the pre-create array for this entire event-handler tick. Handing back the
  // object it already built here — rather than making the caller wait a render and re-derive it
  // — is what let a freshly-created contact's own Sales autofill actually run on the create path
  // the same way picking an EXISTING contact already does.
  const createContactFull = (fields) => {
    let result = { id: null, error: null, contact: null };
    update((d) => {
      const check = validateContactFields(d, fields);
      if (!check.ok) {
        result = { id: null, error: check.error, contact: null };
        return d;
      }
      const nd = addContact(d, fields);
      const nm = (fields.name || "").trim().toLowerCase();
      const match = (nd.contacts || []).find(
        (c) => c.list === fields.list && c.name.toLowerCase() === nm
      );
      result = match
        ? { id: match.id, error: null, contact: match }
        : { id: null, error: "kontak tidak dibuat", contact: null };
      return nd;
    }, { label: `Tambah kontak "${(fields.name || "").trim()}"` });
    return result;
  };

  const recordCategoryVisit = (categoryKey) =>
    update((d) => {
      const key = String(categoryKey || "").trim().toUpperCase();
      if (!key) {
        console.warn("recordCategoryVisit: kunci kategori kosong");
        return d;
      }
      const visits = d.categoryVisits || {};
      return { ...d, categoryVisits: { ...visits, [key]: (visits[key] || 0) + 1 } };
    // Shared books: only a sort-order counter — shown at once, but saved along with the next
    // real change instead of uploading the whole books on every category click.
    }, { label: "Urutan kategori", localOnly: true });

  const handleMergeCar  = (fromName, intoName) => update((d) => logNoOp("handleMergeCar", "mobil tidak digabung — nama sama atau kosong", d, mergeCar(d, fromName, intoName)), { label: `Gabungkan mobil "${fromName}" ke "${intoName}"` });
  const handleRenameCar = (oldName, newName)   => update((d) => logNoOp("handleRenameCar", "mobil tidak diubah namanya — nama baru kosong atau sama", d, renameCar(d, oldName, newName)), { label: `Ubah nama mobil "${oldName}"` });
  const handleDeleteCar = (name)               => update((d) => deleteCar(d, name), { label: `Hapus mobil "${name}"` });

  // Both return `{ok, error}` (mirroring `createContact` above) so a caller — KontakPage's own
  // save flow — can show the REAL reason a save failed and keep the form open, instead of the
  // old silent same-reference no-op (the form closed either way with zero visible feedback).
  // The underlying cause (KontakPage/ContactFormModal don't collect `list`/`personIds` yet) is
  // stage 2 work and is NOT fixed here — this only makes the existing failure visible.
  // `result.contact` (added for the business-linking suggestion feature — see contactMatch.js):
  // the just-created record, found the same validate-then-add-then-find-by-list-and-name way
  // `createContactFull` above already does, so a caller (KontakPage) can run
  // `suggestBusinessMatches` against it without waiting a render for `contacts` to catch up.
  // Purely additive — existing callers checking only `result.ok`/`result.error` are unaffected.
  const handleAddContact = (fields) => {
    let result = { ok: true, error: null, contact: null };
    update((d) => {
      const check = validateContactFields(d, fields);
      if (!check.ok) {
        result = { ok: false, error: check.error, contact: null };
        console.warn(`handleAddContact: ${check.error}`);
        return d;
      }
      const nd = addContact(d, fields);
      const nm = (fields.name || "").trim().toLowerCase();
      const match = (nd.contacts || []).find((c) => c.list === fields.list && c.name.toLowerCase() === nm) || null;
      result = { ok: true, error: null, contact: match };
      return nd;
    }, { label: `Tambah kontak "${(fields.name || "").trim()}"` });
    return result;
  };
  const handleUpdateContact = (id, fields) => {
    let result = { ok: true, error: null };
    update((d) => {
      const current = (d.contacts || []).find((c) => c.id === id);
      if (!current) {
        result = { ok: false, error: "Kontak tidak ditemukan." };
        console.warn(`handleUpdateContact: ${result.error}`);
        return d;
      }
      // Internal-record check runs BEFORE validateContactFields: fields submitted from
      // KontakPage's own form never carry `list`/`personIds`, so validating first would always
      // blame a missing list even when the real, more specific reason is "this is an internal
      // contact and can't be edited here at all."
      if (current.internal) {
        result = { ok: false, error: "Kontak internal tidak bisa diubah di sini." };
        console.warn(`handleUpdateContact: ${result.error}`);
        return d;
      }
      // list is fixed at creation (Contact model redesign, stage 2) — `ContactFormModal` never
      // even renders a control that could send a different one in edit mode (and its own submit
      // omits `list` from the payload entirely), but this stays a real, specifically-named
      // rejection rather than falling through to a generic error, mirroring the `internal` check
      // above. `updateContact` itself refuses the write either way — this is a courtesy message.
      if (fields?.list != null && fields.list !== current.list) {
        result = { ok: false, error: "Daftar (Pusat/Cabang) tidak bisa diubah setelah kontak dibuat." };
        console.warn(`handleUpdateContact: ${result.error}`);
        return d;
      }
      // Merge every field `validateContactFields` actually inspects (list, name, personIds,
      // businessId, labels) against `current` before validating — mirrors exactly how
      // `updateContact` (contacts.js) merges before its OWN internal validate call. `fields` is
      // not guaranteed to be a full form submit: `ContactFormModal`'s real save always sends the
      // complete field set, but business linking's own unlink control (KontakPage's "Lepas dari
      // bisnis"/"Lepas") deliberately sends only `{businessId: null}` — a genuine PARTIAL patch.
      // Validating the raw `fields` object directly (as this used to, list aside) meant that
      // partial patch spuriously failed with "Nama wajib diisi." for a write that never touched
      // the name at all — a real bug this comment exists to prevent reintroducing. `list` stays
      // forced to `current.list` regardless (never taken from `fields` — see the immutability
      // check above).
      const merged = {
        list: current.list,
        name: fields?.name != null ? fields.name : current.name,
        personIds: fields?.personIds != null ? fields.personIds : current.personIds,
        businessId: fields?.businessId !== undefined ? fields.businessId : current.businessId,
        labels: fields?.labels != null ? fields.labels : current.labels,
      };
      const check = validateContactFields(d, merged, { excludeId: id });
      if (!check.ok) {
        result = { ok: false, error: check.error };
        console.warn(`handleUpdateContact: ${check.error}`);
        return d;
      }
      return updateContact(d, id, fields);
    }, { label: (d) => `Kontak "${contactLabel(d, id)}"`, touches: [touchContact(id)] });
    return result;
  };
  // ── Pengeluaran page: STANDALONE costs only (Biaya Lain-Lain, 2026-10-11) ───────────────
  /** Add (id null) or edit one standalone cost; returns {ok, error} so ExpenseFormModal can show
   *  the real reason and stay open (handleAddContact's contract). A cost linked to an existing
   *  transaction is refused here — it's changed only in that transaction's own form (Edit
   *  Transaksi), its one editing surface. One whose transaction was deleted counts as standalone. */
  const handleSaveExpense = (fields, id = null) => {
    const cur = dataRef.current;
    const existing = id ? (cur.expenses || []).find((e) => e.id === id) : null;
    if (id && !existing) return { ok: false, error: "Pengeluaran ini sudah dihapus." };
    if (existing && !isStandaloneExpense(cur, existing)) {
      return { ok: false, error: "Pengeluaran ini tertaut ke transaksi — ubah dari Ubah Transaksi." };
    }
    const next = existing ? { ...existing, ...fields } : { ...fields, txnId: null };
    const check = validateExpense(cur, existing ? { ...next, txnId: null } : next);
    if (!check.ok) return check;
    if (id) update((d) => updateExpense(d, id, fields), { label: "Pengeluaran", touches: [touchExpense(id)] });
    else update((d) => addExpense(d, { ...fields, txnId: null }), { label: "Pengeluaran baru" });
    return { ok: true, error: null };
  };
  const handleDeleteExpense = (id) =>
    update((d) => {
      const e = (d.expenses || []).find((x) => x.id === id);
      if (!e || !isStandaloneExpense(d, e)) return d;
      return deleteExpense(d, id);
    }, { label: "Hapus pengeluaran", touches: [touchExpense(id)] });

  const handleDeleteContact = (id)         => update((d) => logNoOp("handleDeleteContact", "kontak tidak dihapus — masih direferensikan atau kontak internal", d, deleteContact(d, id)), { label: (d) => `Hapus kontak "${contactLabel(d, id)}"`, touches: [touchContact(id)] });

  // Business linking (Contact model redesign, batch 2d — see BusinessLinkModal.js). Unlinking a
  // single entry needs no new handler at all — it's just `handleUpdateContact(id, {businessId:
  // null})`, already wired into KontakPage as `onUpdate`. Linking is the one genuinely new write:
  // it may also need to MINT a new Business record, which no existing handler does.
  //
  // Re-derives "do the involved contacts already span more than one business" itself rather than
  // trusting the caller's own `businessId`/`businessName` choice — BusinessLinkModal already runs
  // the identical check client-side (to decide whether to show its "name this business" step at
  // all), but this is the real write-path guard: never silently merge two existing businesses,
  // regardless of what the UI computed a moment earlier (same "client filters, server re-checks"
  // discipline this app already applies to contact/salesperson anchors elsewhere — see the root
  // doc's own entries).
  //
  // **The "at least two" minimum applies ONLY to minting a brand-new business (bug fix,
  // manual-testing report)** — it used to run unconditionally at the top, which refused
  // re-attaching a single, freshly-detached entry to an EXISTING firm by ticking that firm's own
  // BISNIS row (`contactIds` in that path is legitimately just the one contact; the firm's OTHER
  // members are already linked and untouched). A firm with only one entry is pointless (nothing
  // to combine a total with), but JOINING one that already has two or more is not — the minimum
  // only makes sense while a new `Business` record is about to be created from scratch. Caught
  // by `App.test.js`'s own real-click-path test (28f) — a `KontakPage.test.js` test with a
  // MOCKED `onLinkContacts` passed even with this bug present, since the mock never ran this
  // real validation at all; only a real App mount exercises the function this bug lived in.
  const handleLinkContacts = ({ contactIds, businessId = null, businessName = "" }) => {
    let result = { ok: true, error: null, businessId: null };
    update((d) => {
      const ids = [...new Set(contactIds || [])];
      const involved = ids.map((id) => (d.contacts || []).find((c) => c.id === id)).filter(Boolean);
      if (involved.length === 0) {
        result = { ok: false, error: "Pilih kontak untuk ditautkan." };
        return d;
      }
      const existingIds = [...new Set(involved.map((c) => c.businessId).filter(Boolean))];
      if (existingIds.length > 1) {
        result = { ok: false, error: "Kontak yang dipilih sudah tergabung di bisnis yang berbeda — tidak bisa digabung otomatis." };
        return d;
      }
      let nd = d;
      let bizId = existingIds[0] || businessId || null;
      if (bizId && !businessExists(nd.businesses, bizId)) {
        result = { ok: false, error: "Bisnis tidak ditemukan." };
        return d;
      }
      if (!bizId) {
        if (involved.length < 2) {
          result = { ok: false, error: "Pilih minimal dua kontak untuk membuat bisnis baru." };
          return d;
        }
        const name = (businessName || "").trim();
        if (!name) {
          result = { ok: false, error: "Nama bisnis wajib diisi." };
          return d;
        }
        nd = addBusiness(nd, { name });
        bizId = nd.businesses[nd.businesses.length - 1].id;
      }
      for (const c of involved) {
        if (c.businessId !== bizId) nd = updateContact(nd, c.id, { businessId: bizId });
      }
      result = { ok: true, error: null, businessId: bizId };
      return nd;
    }, { label: "Tautkan kontak ke bisnis" });
    return result;
  };

  const handleRenameCategory = (oldName, newName, newCode) =>
    update((d) => logNoOp("handleRenameCategory", "kategori tidak diubah — nama kosong, tidak ditemukan, atau bentrok nama/kode", d, renameCategory(d, oldName, newName, newCode)), { label: `Ubah kategori "${oldName}"` });
  const handleDeleteCategory = (name) => update((d) => logNoOp("handleDeleteCategory", "kategori tidak dihapus — masih dipakai", d, deleteCategory(d, name)), { label: `Hapus kategori "${name}"` });

  // ── Salespeople CRUD (Phase T1) ─────────────────────────────────────────────────
  const handleUpdateSalesperson = (id, fields) => update((d) => logNoOp("handleUpdateSalesperson", "sales tidak diperbarui — id tidak ditemukan, nama/kode tidak valid, kode bentrok, atau cabang tidak ditemukan", d, updateSalesperson(d, id, fields)), { label: "Data sales", touches: [touchRole(id), touchPersonOfRole(id)] });
  const handleUpdatePerson      = (id, fields) => update((d) => logNoOp("handleUpdatePerson", "orang tidak diperbarui — id tidak ditemukan atau nama kosong", d, updatePerson(d, id, fields)), { label: "Data orang (sales)", touches: [touchPerson(id)] });
  // Person/Role restructure, Stage 3 batch 3b, Part E: a permanent delete, unlike deactivating a
  // role — only ever offered in the UI for a person with zero roles at all (personRefCount===0),
  // which deletePerson itself also guards, so this can never silently no-op against real data.
  const handleDeletePerson     = (id)         => update((d) => logNoOp("handleDeletePerson", "orang tidak dihapus — masih memiliki peran", d, deletePerson(d, id)), { label: "Hapus orang (sales)", touches: [touchPerson(id)] });
  const handleDeleteSalesperson = (id)         => update((d) => logNoOp("handleDeleteSalesperson", "sales tidak dihapus — masih direferensikan", d, deleteSalesperson(d, id)), { label: "Hapus peran sales", touches: [touchRole(id)] });
  // Stage 5 (roles pinned to their location) — the ONE move action for a role that has
  // transactions: deactivates it and picks up (or reactivates) a role at newBranchId, atomically.
  // A no-op covers several distinct refusals at once (id unresolvable, newBranchId invalid or
  // unchanged, or the person already holds an ACTIVE role at the destination) — see
  // moveSalespersonBranch's own header comment in utils/salespeople.js for which is which.
  const handleMoveSalesperson = (id, newBranchId) => update((d) => logNoOp("handleMoveSalesperson", "peran tidak dipindahkan — id tidak ditemukan, cabang tujuan tidak valid/sama dengan cabang saat ini, atau orang ini sudah punya peran aktif di sana", d, moveSalespersonBranch(d, id, newBranchId)), { label: "Pindahkan sales", touches: [touchRole(id)] });

  // ── Branches CRUD (multi-branch cabang feature) ─────────────────────────────────
  // Contact model redesign: a new branch must produce its own internal contact atomically, in
  // the same update tick — ensureInternalContacts is a same-reference no-op when nothing's
  // missing, so this is a plain no-op too when addBranch itself no-opped.
  const handleAddBranch    = (fields)     => update((d) => {
    const nd = logNoOp("handleAddBranch", "cabang tidak disimpan — nama kosong atau duplikat", d, addBranch(d, fields));
    return nd === d ? nd : ensureInternalContacts(nd);
  }, { label: `Tambah cabang "${(fields && fields.name) || ""}"` });
  const handleUpdateBranch = (id, fields) => update((d) => logNoOp("handleUpdateBranch", "cabang tidak diperbarui — id tidak ditemukan, nama kosong, atau duplikat", d, updateBranch(d, id, fields)), { label: "Data cabang", touches: [touchBranch(id)] });
  // deleteBranch only ever succeeds while branchRefCount(d, id) === 0 (zero transactions
  // reference this branch) — the branch's own internal contact can therefore never carry a real
  // contactRefCount either (the only way it gets referenced is via a transaction that also
  // stamps this same branchId), so removing it here alongside the branch is always safe.
  const handleDeleteBranch = (id)         => update((d) => {
    const nd = logNoOp("handleDeleteBranch", "cabang tidak dihapus — masih direferensikan", d, deleteBranch(d, id));
    return nd === d ? nd : removeInternalContactForBranch(nd, id);
  }, { label: "Hapus cabang", touches: [touchBranch(id)] });

  // Person/Role restructure, Stage 3 batch 3a: the ONE creation path for a new role, called
  // from SalespersonFormModal's own two-stage person-then-role flow — either path
  // (KelolaSalesModal's "+ Tambah Sales", or the transaction form's inline "+ Tambah sales
  // baru…" quick-add) ends up here. `fields` is either `{personId, branchId, note?}` (an
  // existing person picking up a new role — no `code`, Stage 4: the code already exists, on the
  // person) or `{name, code, branchId, note?}` (a brand-new person + their first role — `code`
  // is required here since a brand-new person needs one) — `SalespersonFormModal` already
  // resolved which case this is and ran the collision guard before calling this. Both halves
  // (person, then role) commit in ONE update() pass so a person can never persist without its
  // accompanying role — if the final addSalesperson call fails, the whole write reverts to the
  // ORIGINAL `d`, not the intermediate person-added `cur`.
  const createSalespersonRole = (fields) => {
    let newRoleId = null;
    update((d) => {
      let cur = d;
      let personId = fields.personId || null;
      if (!personId) {
        const withPerson = addPerson(d, { name: fields.name, note: fields.note, code: fields.code });
        if (withPerson === d) {
          console.warn("createSalespersonRole: orang tidak dibuat — nama kosong, atau kode tidak valid/bentrok");
          return d;
        }
        cur = withPerson;
        personId = cur.persons[cur.persons.length - 1].id;
      } else if (fields.note != null) {
        cur = updatePerson(d, personId, { note: fields.note });
        if (cur === d) {
          console.warn("createSalespersonRole: catatan tidak disimpan — orang tidak ditemukan");
          return d;
        }
      }
      const nd = addSalesperson(cur, {
        personId, branchId: fields.branchId, commissionRate: fields.commissionRate,
      });
      if (nd === cur) {
        // Bug fix (manual-testing report): `addSalesperson`'s own no-op collapses several
        // distinct reasons into one same-reference return — a genuinely invalid `branchId`, an
        // invalid `commissionRate`, an unresolvable `personId`, OR "this person already holds
        // an ACTIVE role here" (a real, structural refusal at the data layer, not a mistake).
        // The last one is not a failure from THIS function's own intent ("ensure this person has
        // a role at this location") — that's already true. Treating it as an error produced a
        // "tidak dapat ditambahkan" message that contradicted reality whenever a caller's own
        // UI re-offered an add-role action that had already succeeded (e.g. a stale narrowing
        // display re-showing the same offer after the role was already created) — the write
        // itself was never in doubt; only the caller's redundant retry was confused. Recover the
        // existing role's id instead of failing, mirroring `addSalesperson`'s own "reactivate an
        // inactive role instead of erroring" precedent for the sibling case one level up.
        const existingActive = (cur.salespeople || []).find(
          (s) => s.personId === personId && s.branchId === (fields.branchId ?? null) && s.active !== false
        );
        if (existingActive) {
          newRoleId = existingActive.id;
          return cur === d ? d : cur; // still commit a brand-new person, if one was just minted above
        }
        console.warn("createSalespersonRole: sales tidak dibuat — cabang tidak ditemukan, atau orang ini sudah punya peran aktif di lokasi itu");
        return d;
      }
      // Stage 5: addSalesperson may have REACTIVATED an existing (inactive) role at this
      // branch instead of minting a new one — the old "find the id not in beforeIds" diff
      // would miss that case (the reactivated role's id already existed). personId+branchId
      // together uniquely identify the affected role either way, since Part B's own
      // one-role-per-person-per-location invariant guarantees there's exactly one match.
      const match = (nd.salespeople || []).find((s) => s.personId === personId && s.branchId === fields.branchId);
      newRoleId = match ? match.id : null;
      return nd;
    }, { label: `Tambah sales${fields.name ? ` "${fields.name}"` : ""}` });
    return newRoleId;
  };

  // ── Transactions (Phase T1 ledger core) ─────────────────────────────────────────
  /** Human-readable field-by-field diff for a transaction edit's editLog entry ("Detail
   *  Perubahan" style: "Label: old → new"). Resolves contact/salesperson names against `d`
   *  so the log reads names, not raw ids. Line changes are matched by array index —
   *  transaction lines carry no id of their own, same as recipe lines.
   *
   *  Complete-edit-history fix: returns `{ summary, itemChanges }` instead of one flat
   *  string array. `summary` keeps its original shape/wording, now ONLY the non-item
   *  header-level fields (Lokasi/Arah Transfer/Klien/Sales/Tanggal/Waktu/No. Invoice
   *  Supplier/Nama & Alamat Pembeli) — a caller reading `summary` for those fields alone sees
   *  identical strings to before. Line-level changes move to `itemChanges`, one entry PER
   *  CHANGED LINE (never interleaved across items), so `EditLogTimeline` can render a
   *  "Barang Diubah/Ditambahkan/Dihapus: <name>" header per item with its own field diffs
   *  nested underneath instead of a flat list mixing several items' fields together. Each
   *  entry's own `fields` strings drop the item name (redundant — it's now the header). */
  const buildTxnEditDiff = (d, before, after) => {
    const summary = [];
    const contactName = (id) => {
      const c = (d.contacts || []).find((c) => c.id === id);
      return c ? displayContact(c, d.branches) : "—";
    };
    // Person/Role restructure, Stage 2: a role has no name of its own — resolve through the
    // linked person instead of the (now-removed) role.name. Reads CURRENT state at diff-build
    // time, same as before (this was never a frozen historical snapshot — a role's name could
    // already be edited via KelolaSalesModal); the resulting string is what gets baked into
    // this editLog entry, so it's frozen from here on regardless of later renames.
    const spName = (id) => {
      const sp = (d.salespeople || []).find((s) => s.id === id);
      return sp ? (resolveSalespersonName(d.persons, sp) || "—") : "—";
    };
    const push = (label, a, b) => { if (a !== b) summary.push(`${label}: ${a ?? "—"} → ${b ?? "—"}`); };
    const DIRECTION_LABEL = { PUSAT_TO_CABANG: "Pusat → Cabang", CABANG_TO_PUSAT: "Cabang → Pusat" };

    if (before.type === "TRANSFER") {
      push("Arah Transfer", DIRECTION_LABEL[before.transferDirection] || "—", DIRECTION_LABEL[after.transferDirection] || "—");
    } else {
      push("Lokasi", bucketLabel(before.location), bucketLabel(after.location));
    }
    const partyLabel = before.type === "PEMBELIAN" ? "Penjual" : before.type === "TRANSFER" ? "Pihak" : "Pembeli";
    if (before.contactId !== after.contactId) summary.push(`${partyLabel}: ${contactName(before.contactId)} → ${contactName(after.contactId)}`);
    if (before.salespersonId !== after.salespersonId) summary.push(`Sales: ${spName(before.salespersonId)} → ${spName(after.salespersonId)}`);
    push("Tanggal", before.date, after.date);
    push("Waktu", before.time, after.time);
    // Section E1: a due-date-only edit (Tempo Pembayaran) used to save with NO edit-log entry at
    // all — nothing here diffed it, so the entry gate below never fired. "—" = no due date (Lunas).
    push("Jatuh Tempo", before.dueDate ? fmtDateShort(before.dueDate) : "—", after.dueDate ? fmtDateShort(after.dueDate) : "—");
    if (before.type === "PEMBELIAN") push("No. Invoice Penjual", before.supplierInvoiceNo || "—", after.supplierInvoiceNo || "—");
    // A Cabang Pembelian's branch is editable (invariant 19's second exception) — compared by
    // id, so a branch-only correction still produces a real editLog entry. PENJUALAN/TRANSFER
    // branch moves are logged by their own `numberingChange` block instead, never here.
    if (before.type === "PEMBELIAN" && before.branchId !== after.branchId) {
      const branchName = (id) => (id == null ? "—" : resolveBranchName(d.branches, id) || "—");
      summary.push(`Cabang: ${branchName(before.branchId)} → ${branchName(after.branchId)}`);
    }
    // Rename (manual-testing report, issue 3): matches the form field's own new label — see
    // TransactionFormModal.js's identical rename for the full reasoning (`buyerBlock` prints on
    // the invoice/surat jalan, it's not a second copy of the Pembeli contact named just above).
    push("Nama Tercetak", before.buyerBlock?.name || "—", after.buyerBlock?.name || "—");
    push("Alamat Tercetak", before.buyerBlock?.address || "—", after.buyerBlock?.address || "—");

    // Section E1: lines are matched by WHICH ITEM they are, not by position — removing the
    // first of three lines used to read as two fake "swaps" plus a removal. See utils/lineDiff.js.
    const itemChanges = diffTxnLines(before.lines, after.lines, fmtQty);
    return { summary, itemChanges };
  };

  /** Payment diff for editTransaction's own patch.payments — a sibling to buildTxnEditDiff,
   *  but with a DIFFERENT match key: payments carry a stable `id` (stamped at creation/
   *  recordPayment/edit time), so they're matched by id, never by array index/position the
   *  way lines are. Wording mirrors recordPayment's own exact strings (the "added" case) and
   *  the retired correctPayment's own exact field-diff wording (the "corrected" case), so a
   *  reader of editLog can't tell which write path produced a given paymentChanges entry. */
  const buildPaymentDiff = (beforePayments, afterPayments) => {
    const paymentChanges = [];
    const beforeById = new Map((beforePayments || []).map((p) => [p.id, p]));
    const afterIds = new Set((afterPayments || []).map((p) => p.id));

    for (const after of afterPayments || []) {
      const before = beforeById.get(after.id);
      if (!before) {
        const addedFields = [`Rp 0 → Rp ${fmtQty(after.amount)}`];
        // Two-bucket payment split: a newly-added row carrying a split gets the same two
        // "Untuk..." lines a payment_corrected entry would — via paymentSplit()'s own
        // fallback for the "before" side (nothing existed, so both read as 0).
        if (typeof after.splitBarang === "number" && typeof after.splitTagihanPotongan === "number") {
          addedFields.push(`Untuk Barang: Rp 0 → Rp ${fmtQty(after.splitBarang)}`);
          addedFields.push(`Untuk Tagihan & Potongan: Rp 0 → Rp ${fmtQty(after.splitTagihanPotongan)}`);
        }
        paymentChanges.push({
          kind: "payment_added",
          label: paymentMethodText(after),
          fields: addedFields,
        });
        continue;
      }
      const fields = [];
      if (before.method !== after.method || before.methodNote !== after.methodNote) {
        fields.push(`Metode: ${paymentMethodText(before)} → ${paymentMethodText(after)}`);
      }
      if (before.amount !== after.amount) {
        fields.push(`Jumlah: Rp ${fmtQty(before.amount)} → Rp ${fmtQty(after.amount)}`);
      }
      if (before.paidAt !== after.paidAt) {
        fields.push(`Tanggal Bayar: ${fmtDate(before.paidAt)} → ${fmtDate(after.paidAt)}`);
      }
      // Two-bucket payment split: compared via paymentSplit()'s own fallback-resolved values,
      // so a row gaining its FIRST split correctly shows "Rp {amount} → Rp {new}" on the
      // Barang line (the implicit 100%-Barang value it always had), never a fabricated Rp 0.
      // An unrelated field correction (date/method/amount only) leaves both split fields
      // untouched — App.js's editTransaction only overwrites them when a patch row explicitly
      // supplies BOTH — so this comparison is a genuine no-op for that case.
      if ((before.splitBarang ?? null) !== (after.splitBarang ?? null) || (before.splitTagihanPotongan ?? null) !== (after.splitTagihanPotongan ?? null)) {
        const beforeSplit = paymentSplit(before);
        const afterSplit = paymentSplit(after);
        fields.push(`Untuk Barang: Rp ${fmtQty(beforeSplit.barang)} → Rp ${fmtQty(afterSplit.barang)}`);
        fields.push(`Untuk Tagihan & Potongan: Rp ${fmtQty(beforeSplit.tagihanPotongan)} → Rp ${fmtQty(afterSplit.tagihanPotongan)}`);
      }
      if (fields.length > 0) {
        paymentChanges.push({ kind: "payment_corrected", label: paymentMethodText(after), fields });
      }
    }
    for (const before of beforePayments || []) {
      if (!afterIds.has(before.id)) {
        paymentChanges.push({
          kind: "payment_removed",
          label: paymentMethodText(before),
          fields: [`Rp ${fmtQty(before.amount)} → Rp 0`],
        });
      }
    }
    return paymentChanges;
  };

  /** Cost-item diff for editTransaction's own patch.costItems (Brief 3) — mirrors
   *  buildPaymentDiff exactly: matched by stable `id` (never array index), three kinds
   *  (cost_added/cost_corrected/cost_removed), same `{kind, label, fields}` shape. Diffs
   *  against the ALREADY-resolved `afterCostItems` (the id-matched-or-abort loop earlier in
   *  editTransaction) — no second resolution pass. Each entry also carries the row's own
   *  `type` ("biaya"|"tagihan"|"tagihanSupplier"), which the UI (EditLogTimeline.js) uses to
   *  decide whether this change moved Total — that decision lives entirely in the shared
   *  MoneyBefore/MoneyAfter snapshot already attached to the entry (txnTotal already includes
   *  costItems as of Brief 2), never duplicated here. `txnType` resolves the human label for
   *  a "Tipe: X → Y" field line when a row's own type is changed in place. */
  const buildCostItemDiff = (beforeCostItems, afterCostItems, txnType) => {
    const costChanges = [];
    const beforeById = new Map((beforeCostItems || []).map((c) => [c.id, c]));
    const afterIds = new Set((afterCostItems || []).map((c) => c.id));
    const typeLabel = (t) => costItemTypeLabel(t, txnType);

    for (const after of afterCostItems || []) {
      const before = beforeById.get(after.id);
      if (!before) {
        costChanges.push({
          kind: "cost_added",
          label: after.description,
          type: after.type,
          fields: [`Rp 0 → Rp ${fmtQty(after.amount)}`],
        });
        continue;
      }
      const fields = [];
      if (before.description !== after.description) {
        fields.push(`Deskripsi: ${before.description} → ${after.description}`);
      }
      if (before.amount !== after.amount) {
        fields.push(`Jumlah: Rp ${fmtQty(before.amount)} → Rp ${fmtQty(after.amount)}`);
      }
      if (before.type !== after.type) {
        fields.push(`Tipe: ${typeLabel(before.type)} → ${typeLabel(after.type)}`);
      }
      if (fields.length > 0) {
        costChanges.push({ kind: "cost_corrected", label: after.description, type: after.type, fields });
      }
    }
    for (const before of beforeCostItems || []) {
      if (!afterIds.has(before.id)) {
        costChanges.push({
          kind: "cost_removed",
          label: before.description,
          type: before.type,
          fields: [`Rp ${fmtQty(before.amount)} → Rp 0`],
        });
      }
    }
    return costChanges;
  };

  /** Biaya Lain-Lain (2026-10-11) in the edit log: a transaction's linked costs, shaped like cost
   *  items (type "biayaLain") so buildCostItemDiff logs them in the same costChanges list, with
   *  the same added/corrected/removed wording — they're changed in the same form, so they're
   *  recorded in the same Riwayat Perubahan. */
  const expensesAsCostItems = (expenses, txnId) =>
    expensesForTxn(expenses, txnId).map((e) => ({ id: e.id, description: expenseLabel(e), amount: e.amount, type: "biayaLain" }));

  /** Frozen money snapshot for an editLog entry — `total`/`sisa`/`dibayar` computed from the
   *  ACTUAL transaction state at that moment (never recomputed later), the same "true
   *  historical record" contract `summary`/`itemChanges` strings already are. Mirrors
   *  `TransactionDetailModal.js`'s own `paid = total - sisa` pattern — no separate "paid"
   *  helper needed in utils/txnStatus.js. */
  const txnMoneySnapshot = (t) => {
    const total = txnTotal(t);
    const sisa = txnSisa(t);
    return { total, sisa, dibayar: total - sisa };
  };

  /** Determine a Penjualan/Transfer's numbering "anchor" — PUSAT or CABANG — used to pick
   *  which counter/format to mint (Phase T2a, PRD §3/§4). A salesperson is required on BOTH
   *  anchors (invariant 12, revised — a Pusat sale genuinely involves one too, it's just never
   *  shown on the printed Pusat documents); only `branchId` stays anchor-conditional (null for
   *  Pusat, derived from the salesperson for Cabang). For TRANSFER, `location` is always the
   *  SOURCE bucket, so the anchor follows `transferDirection`, not `location` read directly.
   *  PEMBELIAN has no anchor — it mints nothing and never carries a salesperson. */
  const txnAnchor = (type, location, transferDirection) => {
    if (type === "TRANSFER") return transferDirection === "PUSAT_TO_CABANG" ? "PUSAT" : "CABANG";
    if (type === "PENJUALAN") return location;
    return null;
  };

  /** Create a transaction: for TRANSFER, first resolves (find-or-creates) the reserved
   *  branch contact for the direction and snapshots its name/address as buyerBlock; mints an
   *  invoice number by anchor (PENJUALAN/TRANSFER only) — BOTH a PUSAT anchor (via
   *  `mintPusatNumber`) and a CABANG anchor (via `mintCabangNumber`) now REQUIRE a valid
   *  salesperson, aborting (no write) if missing (invariant 12, revised — a Pusat sale
   *  genuinely has one too, it just never appears on a printed Pusat document; no
   *  grandfathering for old data with no salesperson, per the client's own instruction).
   *  `branchId` stays the one genuinely anchor-conditional field — null for Pusat regardless of
   *  which salesperson is picked, derived from the salesperson's own branch for Cabang. Applies
   *  the stock effect (+1, both buckets atomically for
   *  TRANSFER — utils/txnStock.js already handles this by type). **Phase T2b**: validates
   *  `draft.payments` (the form's own multi-method rows) via `utils/payments.js`'s
   *  `validatePayments(payments, total, intent)` before writing anything — aborts (no write,
   *  no mint side effect to undo, since this check runs before either mint function is called)
   *  if the rows don't match the Lunas/Belum-Lunas intent (invariants 15/16). Recording
   *  payments after creation is still later work (T3) — only creation-time rows are ever
   *  written here. **Multi-branch cabang feature**: also derives `branchId` — for a CABANG
   *  anchor, copied from the resolved salesperson's OWN `branchId` at this moment (never a
   *  live join later); for a PUSAT_TO_CABANG transfer (the salesperson's own branch doesn't
   *  imply a destination — Pusat isn't a branch), read from `draft.branchId` and REQUIRED,
   *  aborting (no write) if missing/invalid; every
   *  other case (PUSAT-anchored PENJUALAN) leaves it `null`. **Post-launch fix**: a
   *  CABANG-anchored PEMBELIAN also now REQUIRES an explicit `draft.branchId` (no salesperson
   *  to derive one from — PEMBELIAN never mints/anchors), aborting (no write) if
   *  missing/invalid; a PUSAT-anchored PEMBELIAN leaves it `null`, same as before. **Cost
   *  items (Brief 1: stored/validated; Brief 2: wired into total)**: `draft.costItems`
   *  (biaya/tagihan rows, see utils/costItems.js) is validated FIRST — before `total` is even
   *  computed, so a malformed `amount` can never reach the sum — then folded into `total` via
   *  `txnTotal({lines, costItems})` (tagihan/tagihanSupplier raise it, biaya never does),
   *  which is what the Lunas-mismatch `paymentsCheck` below is actually validated against.
   *  `draft` is built by TransactionFormModal: { type, location, transferDirection?, contactId?,
   *  salespersonId?, branchId?, supplierInvoiceNo?, date, time, buyerBlock?, lines, isLunas,
   *  dueDate, payments, costItems }. */
  const createTransaction = (draft) => {
    let created = null;
    update((d) => {
      let cur = d;
      let contactId = draft.contactId || null;
      let buyerBlock = draft.buyerBlock || null;
      if (draft.type === "TRANSFER") {
        // PUSAT_TO_CABANG's internal contact is destination-branch-specific — draft.branchId is
        // already the picked destination at this point (the form's own required field for this
        // direction, validated further below); CABANG_TO_PUSAT needs no branchId at all.
        const res = resolveTransferContact(cur, draft.transferDirection, draft.branchId);
        cur = res.data;
        contactId = res.contact ? res.contact.id : null;
        buyerBlock = res.contact
          ? { name: displayContact(res.contact, cur.branches), address: defaultAddressText(res.contact) }
          : null;
      }
      // Contacts/salespeople link-up (write-path guard): a Contact's own `list` must match the
      // transaction's anchor — a PUSAT contact can never end up on a Cabang-anchored transaction
      // and vice versa, regardless of what the form sent (the same "the write path checks it
      // too" discipline invariant 12's own anchor/branchId guards already follow — this one did
      // NOT already exist before this pass). Never checked for TRANSFER — its own contactId is
      // always resolved server-side just above, from the real internal contact for this exact
      // direction, never the client's own draft.contactId. **Only fires when the contact
      // actually RESOLVES** — an unresolvable contactId is a separate, pre-existing concern this
      // guard was never asked to newly enforce (this app's own test fixtures widely use a
      // deliberately non-existent placeholder id in tests unrelated to contacts at all, e.g.
      // after the v24→v25 migration's own unconditional `contacts` wipe — adding an existence
      // check here would reject all of those for a reason they have nothing to do with).
      //
      // **Internal-contact guard (closes a real gap — TransactionFormModal's own `pickableContacts`
      // already filters `!c.internal`, `App.js:270`-equivalent, but that's a client-side picker
      // filter only; nothing at the write path stopped a PENJUALAN/PEMBELIAN from being saved
      // against an internal contact if a picker bug ever let one through — confirmed by direct
      // read before this fix, not assumed).** An internal contact is an app-maintained TRANSFER
      // stand-in (`resolveTransferContact`), never a real counterparty — checked BEFORE the
      // `list` check above so the real reason ("this is an internal contact") is never masked by
      // a coincidental list match (an internal contact's own `list` is a real PUSAT/CABANG value
      // and could easily equal `draft.location`). Never fires for TRANSFER — that IS this
      // contact kind's only legitimate use, resolved server-side just above, never through this
      // branch at all.
      if (draft.type !== "TRANSFER" && contactId) {
        const contact = (cur.contacts || []).find((c) => c.id === contactId);
        if (contact && contact.internal) {
          console.warn("createTransaction: kontak internal tidak bisa dipakai untuk transaksi selain Transfer");
          return d; // abort — no mutation; created stays null
        }
        if (contact && contact.list !== draft.location) {
          console.warn("createTransaction: kontak yang dipilih tidak sesuai dengan lokasi transaksi (Pusat/Cabang)");
          return d; // abort — no mutation; created stays null
        }
      }
      const createdAt = new Date().toISOString();
      let invoiceNumber = null;
      let txnCounters = cur.txnCounters;
      let salespersonId = null;
      let branchId = null;

      if (draft.type === "PENJUALAN" || draft.type === "TRANSFER") {
        const anchor = txnAnchor(draft.type, draft.location, draft.transferDirection);
        if (anchor === "PUSAT") {
          // Invariant 12 (revised): a Pusat anchor now REQUIRES a valid salesperson too, same
          // as Cabang — the client confirmed Pusat sales genuinely involve one, the printed
          // Pusat documents simply never show it. No grandfathering: this aborts the same way
          // the Cabang branch below always has.
          const sp = (cur.salespeople || []).find((s) => s.id === draft.salespersonId);
          if (!sp) {
            console.warn("createTransaction: transaksi pusat wajib memiliki sales");
            return d; // abort — no mutation; created stays null
          }
          // Person/Role restructure, Stage 3 batch 2: a NEW transaction is always "picking a
          // role for the first time" — there is no `before` to fall back on the way an edit
          // has — so a deactivated role is simply not selectable here, full stop.
          if (sp.active === false) {
            console.warn("createTransaction: sales yang dipilih sudah tidak aktif");
            return d; // abort — no mutation; created stays null
          }
          // A picker bug should never be able to put the wrong KIND of role on a transaction —
          // this refuses regardless of what the UI actually sent, the same "the write path
          // checks it too" discipline every other guard here already follows. A Pusat anchor
          // requires a role whose OWN `branchId` is `null`; a branch role (even an otherwise
          // valid, active one) is not a Pusat role, full stop.
          if (sp.branchId !== null) {
            console.warn("createTransaction: sales yang dipilih bukan peran Pusat");
            return d; // abort — no mutation; created stays null
          }
          salespersonId = sp.id;
          // branchId stays null regardless of the salesperson's own branch — Pusat is not a
          // cabang, and this must never feed into invariant 19's branch attribution.
          const minted = mintPusatNumber(cur, draft.date);
          invoiceNumber = minted.invoiceNumber;
          txnCounters = minted.updatedCounters;
          if (draft.type === "TRANSFER") {
            // Pusat->Cabang transfer: no branch-of-its-own to derive from a salesperson (a
            // Pusat sale's own salesperson still doesn't imply a destination branch) — this is
            // the ONE case needing an explicit destination-branch pick from the form
            // (multi-branch cabang feature, decision 1).
            const branch = (cur.branches || []).find((b) => b.id === draft.branchId);
            if (!branch) {
              console.warn("createTransaction: transfer pusat->cabang wajib memiliki cabang tujuan");
              return d; // abort — no mutation; created stays null
            }
            branchId = branch.id;
          }
          // A PUSAT-anchored PENJUALAN carries no branch at all — branchId stays null.
        } else {
          const sp = (cur.salespeople || []).find((s) => s.id === draft.salespersonId);
          if (!sp) {
            console.warn("createTransaction: transaksi cabang wajib memiliki sales");
            return d; // abort — no mutation; created stays null
          }
          // Same "no `before` to fall back on" reasoning as the Pusat branch above.
          if (sp.active === false) {
            console.warn("createTransaction: sales yang dipilih sudah tidak aktif");
            return d; // abort — no mutation; created stays null
          }
          // Mirror of the Pusat-side guard above: a Cabang anchor requires a role that actually
          // HAS a branch — a Pusat role (`branchId: null`) here would otherwise silently derive
          // `branchId: null` below, corrupting the one thing a Cabang-anchored transaction must
          // always have.
          if (sp.branchId === null) {
            console.warn("createTransaction: sales yang dipilih bukan peran cabang");
            return d; // abort — no mutation; created stays null
          }
          const minted = mintCabangNumber(cur, resolveSalespersonCode(cur.persons, sp), draft.date);
          invoiceNumber = minted.invoiceNumber;
          txnCounters = minted.updatedCounters;
          salespersonId = sp.id;
          // Derived from the salesperson's CURRENT branch at creation time — copied onto the
          // transaction, never a live join (multi-branch cabang feature, decision 1). No extra
          // abort guard needed here: a salesperson always has a valid branchId by construction
          // (utils/salespeople.js validates it on write).
          branchId = sp.branchId || null;
        }
      } else if (draft.type === "PEMBELIAN" && draft.location === "CABANG") {
        // Post-launch fix: a Cabang-anchored PEMBELIAN never had a salesperson to derive a
        // branch from (PEMBELIAN never mints/anchors at all) — it now carries a plain,
        // explicitly-picked branchId instead, required and immutable after creation (same
        // invariant 19 `editTransaction` already enforces type-agnostically).
        const branch = (cur.branches || []).find((b) => b.id === draft.branchId);
        if (!branch) {
          console.warn("createTransaction: pembelian cabang wajib memiliki cabang");
          return d; // abort — no mutation; created stays null
        }
        branchId = branch.id;
      }

      const costItemsCheck = validateCostItems(draft.costItems || [], draft.type);
      if (!costItemsCheck.ok) {
        console.warn("createTransaction: biaya/tagihan tidak valid — " + costItemsCheck.error);
        return d; // abort — no mutation; created stays null
      }
      const total = txnTotal({ lines: draft.lines, costItems: draft.costItems || [] });
      const totalCheck = validateTxnTotal(total);
      if (!totalCheck.ok) {
        console.warn("createTransaction: total tidak valid — " + totalCheck.error);
        return d; // abort — no mutation; created stays null
      }
      const intent = draft.isLunas ? "LUNAS" : "BELUM_LUNAS";
      const paymentsCheck = validatePayments(draft.payments || [], total, intent);
      if (!paymentsCheck.ok) {
        console.warn("createTransaction: pembayaran tidak valid — " + paymentsCheck.error);
        return d; // abort — no mutation; created stays null
      }

      // Two-bucket payment split. Per-row split-shape (any row carrying BOTH split fields must
      // sum to its own amount, both non-negative) and Check 1 (checkBucketOverAllocation) are
      // UNCONDITIONAL, defense-in-depth guards — mirroring paymentsCheck/costItemsCheck's own
      // "always validate server-side regardless of what the UI would normally send" posture —
      // even though a no-bucket creation's real form never sends split fields at all. Check 2
      // (per-bucket completeness against the two Lunas/Belum-Lunas toggles) only makes sense
      // once a live Tagihan & Potongan bucket exists — see utils/paymentSplit.js's own header
      // comment for why "no bucket" must stay byte-identical to today's single-toggle behavior.
      const draftTxnLike = { lines: draft.lines, costItems: draft.costItems || [], payments: draft.payments || [] };
      for (const p of draft.payments || []) {
        if (typeof p.splitBarang === "number" && typeof p.splitTagihanPotongan === "number" && !validateSplitSum(p.amount, p.splitBarang, p.splitTagihanPotongan)) {
          console.warn("createTransaction: pembagian pembayaran tidak sesuai jumlah");
          return d; // abort — no mutation; created stays null
        }
      }
      const overAlloc = checkBucketOverAllocation(draftTxnLike);
      if (!overAlloc.ok) {
        console.warn("createTransaction: " + overAlloc.violations.map(bucketOverAllocationMessage).join(" "));
        return d; // abort — no mutation; created stays null
      }
      // Gated on BOTH buckets being genuinely positive (the same `bothBucketsLive` criterion the
      // single-toggle redesign uses client-side to decide whether to send `draft.barangLunas`/
      // `draft.tagihanPotonganLunas` at all) — NOT `tagihanPotonganOwed > 0` alone (the
      // pre-redesign condition). Once either bucket clamps to zero/negative (e.g. `goodsTotal
      // === 0`), the client correctly stops sending BOTH toggle fields; the old, narrower gate
      // would still run Check 2 against `undefined` intents for whichever bucket's own owed is
      // still positive, reading `undefined` as Belum Lunas and genuinely failing a transaction
      // the client already proved valid — a real bug caught by this app's own tests.
      if (barangOwed(draftTxnLike) > 0 && tagihanPotonganOwed(draftTxnLike) > 0) {
        const barangCheck = validateBucketCompleteness("Barang", barangPaid(draftTxnLike), barangOwed(draftTxnLike), !!draft.barangLunas);
        if (!barangCheck.ok) {
          console.warn("createTransaction: " + barangCheck.error);
          return d; // abort — no mutation; created stays null
        }
        const tagihanCheck = validateBucketCompleteness("Tagihan & Potongan", tagihanPotonganPaid(draftTxnLike), tagihanPotonganOwed(draftTxnLike), !!draft.tagihanPotonganLunas);
        if (!tagihanCheck.ok) {
          console.warn("createTransaction: " + tagihanCheck.error);
          return d; // abort — no mutation; created stays null
        }
      }

      const txn = {
        id: generateId(),
        type: draft.type,
        location: draft.location,
        transferDirection: draft.transferDirection || null,
        contactId,
        salespersonId,
        branchId,
        invoiceNumber,
        supplierInvoiceNo: draft.type === "PEMBELIAN" ? (draft.supplierInvoiceNo || "") : null,
        date: draft.date,
        time: draft.time,
        createdAt,
        dueDate: draft.isLunas ? null : (draft.dueDate || null),
        buyerBlock,
        lines: draft.lines,
        packing: { suratJalanRows: [] },
        addressOverrides: {},
        payments: (draft.payments || []).map((p) => ({
          id: generateId(), at: createdAt,
          // paidAt: real, load-bearing default, not a leftover — combineDateTime (the form's
          // own helper) never sends a blank value, but this is the actual API boundary any
          // caller (including a hand-built test payload) goes through, so it's what genuinely
          // implements "default paidAt to now when not supplied."
          paidAt: p.paidAt || createdAt,
          amount: p.amount, method: p.method, methodNote: p.methodNote,
          // Split fields are stamped only when both are actually present on the incoming row —
          // a no-bucket creation never sends them, so a plain payment stays exactly as before.
          ...(typeof p.splitBarang === "number" && typeof p.splitTagihanPotongan === "number"
            ? { splitBarang: p.splitBarang, splitTagihanPotongan: p.splitTagihanPotongan }
            : {}),
        })),
        costItems: (draft.costItems || []).map((c) => ({
          id: generateId(), description: c.description, amount: c.amount, type: c.type,
        })),
        returs: [],
        editLog: [],
      };
      // Corrective pass Piece 3: PERTAMA is a real, fully-itemized editLog[0] entry — a diff
      // FROM an empty/nothing transaction TO the just-built `txn`, reusing the EXACT SAME
      // diff-building closures editTransaction already uses below, rather than new,
      // PERTAMA-specific formatting logic. `emptyBefore` only ever needs a real `.lines` array
      // (the item-diff loop's only real input); `buildTxnEditDiff`'s `summary` half is
      // deliberately DISCARDED here, never stored — it reads header fields like `.location` via
      // `utils/transformations.js`'s `bucketLabel`, which is NOT null-safe against an empty
      // skeleton (`bucketLabel(null) === "Pusat"`, colliding with a real Pusat value and either
      // hiding a real Lokasi diff or fabricating a misleading "Pusat → Cabang" line for a
      // Cabang-anchored transaction) — rather than patch that shared helper for a case real
      // edits never hit, PERTAMA simply itemizes the three things actually asked for (goods
      // lines, payment methods, cost items), matching `buildTxnEditDiff`'s own `itemChanges`
      // half exactly. `buildTxnEditDiff`'s first argument is named `d` in its own definition and
      // is the APP-WIDE DATA object (used only inside the discarded `summary` half, for
      // contactName/spName lookups) — never a transaction, despite the function's own
      // `before`/`after` parameter names suggesting otherwise. Passing `cur` (this closure's own
      // in-scope data object) here is correct and safe, but only because `itemChanges` never
      // reads this first argument at all — its identity is inert for this call.
      const emptyBefore = { type: txn.type, lines: [] };
      const { itemChanges: creationItemChanges } = buildTxnEditDiff(cur, emptyBefore, txn);
      const creationPaymentChanges = buildPaymentDiff([], txn.payments);
      const creationCostChanges = buildCostItemDiff([], txn.costItems, txn.type);
      // Section E2: one id for this save, on its edit-log entry AND its stock-history entries.
      const editId = generateId();
      txn.editLog = [{
        at: createdAt,
        editId,
        isCreation: true, // discriminant EditLogTimeline.js uses to tell a real creation entry
        // apart from an old transaction's own genuine first EDIT landing at editLog[0] (pre-
        // Piece-3 data, never migrated/backfilled) — see that file's own header comment.
        summary: [],
        itemChanges: creationItemChanges,
        paymentChanges: creationPaymentChanges,
        costChanges: creationCostChanges,
        moneyBefore: txnMoneySnapshot({ lines: [], costItems: [], payments: [], returs: [] }),
        moneyAfter: txnMoneySnapshot(txn),
      }];
      const withStock = applyTxnStockDelta(cur, txn, +1);
      const stockEntries = transactionEntries(txn, withStock.items, 1, "", editId);
      const withTxn = {
        ...withStock,
        transactions: [...(withStock.transactions || []), txn],
        txnCounters,
        history: [...withStock.history, ...stockEntries],
      };
      // Biaya Lain-Lain (2026-10-11): the form's "Biaya Lain-Lain" rows are saved in this same
      // change, linked to this transaction and dated/located like it (utils/expenses.js).
      const synced = syncLinkedExpenses(withTxn, txn, draft.expenses || []);
      if (!synced.ok) {
        console.warn("createTransaction: biaya lain-lain tidak valid — " + synced.error);
        return d;
      }
      const expenseChanges = buildCostItemDiff([], expensesAsCostItems(synced.data.expenses, txn.id), txn.type);
      const finalTxn = expenseChanges.length === 0 ? txn : {
        ...txn,
        editLog: txn.editLog.map((entry, i) => (i === 0 ? { ...entry, costChanges: [...(entry.costChanges || []), ...expenseChanges] } : entry)),
      };
      created = finalTxn;
      return {
        ...synced.data,
        transactions: synced.data.transactions.map((t) => (t.id === txn.id ? finalTxn : t)),
      };
    }, { label: `${({ PENJUALAN: "Penjualan", PEMBELIAN: "Pembelian", TRANSFER: "Transfer" })[draft.type] || "Transaksi"} baru` });
    return created;
  };

  /** Edit an existing transaction: reverses the old stock effect, applies the new one, and
   *  by default NEVER re-mints the invoice number (PRD invariant 8 — numbers are immutable
   *  once minted). **Invariant 18 (Phase T2a)**: `location` and `transferDirection` are
   *  immutable after creation — an edit attempting to change either aborts with no write,
   *  BEFORE any other logic runs, so no phantom editLog entry is ever created for a rejected
   *  no-op. This also keeps invariant 12 stable on edit: since the anchor (derived from the
   *  now-immutable `location`/`transferDirection`) can never change, `salespersonId` is
   *  recomputed the same way as create — a valid salesperson is now REQUIRED on BOTH anchors
   *  (invariant 12, revised — no grandfathering for an old Pusat transaction whose
   *  `salespersonId` is still null; editing it now requires picking a real one) — so an edit
   *  can never leave a transaction violating invariant 12 either. **A role's `active` flag
   *  (Person/Role restructure, Stage 3 batch 2) is deliberately ASYMMETRIC between create and
   *  edit**: `createTransaction` refuses a deactivated role outright (there is no `before` — a
   *  new transaction is always a first-time pick). This function instead only refuses a
   *  deactivated role when the edit is a genuine REASSIGNMENT — `sp.id !== before.salespersonId`
   *  — so resubmitting the SAME role a transaction already had keeps working even after that
   *  role is deactivated later; the transaction's own history must keep resolving and stay
   *  editable regardless of what happens to the role afterward. **Invariant 19 (multi-branch
   *  cabang feature)**: `branchId` is immutable after creation — a creation-time snapshot of the
   *  salesperson's branch at that moment, never a live join — WITH two deliberate, scoped
   *  exceptions: a CABANG-anchored PEMBELIAN's hand-picked branch may be corrected directly (see
   *  the guard at the top of this function), and the salesperson-correction re-mint feature: when this same edit
   *  reassigns a CABANG-anchored Penjualan's or a CABANG_TO_PUSAT Transfer's own `salespersonId`
   *  to a genuinely DIFFERENT PERSON (`sp.personId !== (before's own role)'s personId` — compared
   *  by PERSON, not role, see the bug fix below) whose real branch differs from the transaction's
   *  current `branchId` (`sp.branchId !== before.branchId`), the invoice number re-mints under
   *  `mintCabangNumber(cur, resolveSalespersonCode(cur.persons, sp), patch.date)` — the
   *  transaction's own date field, the same "which month" rule creation-time minting already
   *  uses, never wall-clock "now". This does NOT loosen the rule for any other case: a
   *  salesperson later moving to a different branch via Kelola Sales, with THIS transaction's own
   *  `salespersonId` never touched, still never retroactively changes `branchId` here — only an
   *  actual reassignment to a different person, whose branch differs, triggers a re-mint.
   *  Reassigning to a different salesperson at the SAME branch does not re-mint either. The
   *  old, now-orphaned invoice number is simply abandoned — counters never decrement or
   *  reclaim a number regardless (invariant 8), so this is safe by the same standing rule
   *  that already makes a deleted transaction's numbering gap safe.
   *
   *  **Bug fix ("the re-mint trigger compares roles, not people")**: `branchId`'s own
   *  re-derivation and the re-mint used to be ONE compound condition, `sp.id !==
   *  before.salespersonId && sp.branchId !== before.branchId` — `sp.id` is a ROLE id, but the
   *  invoice counter is keyed on the PERSON's own `code`, so the SAME person moving roles
   *  (Budi-at-TMM -> Budi-at-AMIN) tripped the role-id half and wrongly re-minted a number under
   *  Budi's own counter for no reason — nobody being billed changed, only which branch the
   *  transaction is anchored to. Now split into two independent booleans: `branchChanged`
   *  (`sp.branchId !== before.branchId`) alone re-derives `branchId` — the stored anchor must
   *  always track the picked role's real branch regardless of whether the person also changed;
   *  `personChanged && branchChanged` (`personChanged` compares `sp.personId` against the
   *  PREVIOUS role's own `personId`, looked up fresh) alone re-mints — the documented exception's
   *  own wording, "a genuinely different PERSON whose branch differs," now actually means person
   *  identity, not role identity. A same-branch person reassignment is unaffected — `branchChanged`
   *  is false there, so neither `branchId` nor the invoice number move, exactly as documented
   *  above. `numberingChange` is now built on `branchChanged` ALONE (not only on a real re-mint)
   *  — a branch-only move still changes a field invariant 19 otherwise treats as immutable, and
   *  needs its own visible, logged trace the same as a full re-mint does; `invoiceNumber` on both
   *  sides of that record is simply equal in the no-remint case — see
   *  `EditLogTimeline.js`'s `NumberingChangeBlock` for how it renders that case distinctly.
   *  Logged via a dedicated `numberingChange` editLog field (see `buildTxnEditDiff`'s sibling
   *  construction below and `EditLogTimeline.js`'s `NumberingChangeBlock`). Appends a
   *  human-readable field-by-field diff to editLog when anything actually changed. `packing` is
   *  never touched here (written only by the dedicated Pengepakan action).
   *
   *  Payment-correction rework: `patch.payments` (optional, a FULL replacement array — a row
   *  carrying an existing `id` is a correction/unchanged, a row with no `id` is new and gets
   *  stamped `{id, at}` server-side, mirroring createTransaction/recordPayment's own
   *  stamping) and `patch.dueDate` (optional) are now real, write-through fields — diffed via
   *  buildPaymentDiff and validated (see below) before any mutation, same as every other
   *  field here. Omitting `patch.payments` entirely leaves `payments` untouched (the old
   *  behavior, still relied on by every line/header-only edit). `patch.isLunas` is accepted
   *  PURELY as a validation-time intent signal — like status/total/sisa everywhere else in
   *  this app, it is NEVER persisted; TransactionFormModal's edit-mode Lunas/Belum Lunas
   *  toggle is a pure UI signal that must never mutate `payments` on its own (see that
   *  component's own `toggleLunas`) — this is what lets Simpan compare "what the toggle
   *  claims" against "what the real rows sum to" and block on a mismatch, exactly as create
   *  mode already does.
   *
   *  Cost items (Brief 1: stored/validated; Brief 2: wired into total): `patch.costItems`
   *  (optional) is resolved the SAME id-matched-or-abort way as `patch.payments` (a row with
   *  an `id` corrects the matching stored row, a row with no `id` is new and gets stamped
   *  `{id}` server-side, a row missing from the patch is a deletion) and validated via
   *  `validateCostItems`. Omitting `patch.costItems` entirely leaves `costItems` untouched.
   *  **Resolved BEFORE `afterTotal` is computed** (a real ordering fix from Brief 2 — it used
   *  to sit after the payments/Lunas check, so `afterTotal` never saw a same-edit costItems
   *  change at all) so `txnTotal({lines, costItems: afterCostItems})` — and therefore the
   *  Lunas-mismatch check right after it — reflects any tagihan/tagihanSupplier change made in
   *  THIS SAME save. **Brief 3**: now also diffed via `buildCostItemDiff` into a `costChanges`
   *  editLog field, alongside `itemChanges`/`paymentChanges` — see that function's own comment. */
  const editTransaction = (id, patch) =>
    update((d) => {
      const before = (d.transactions || []).find((t) => t.id === id);
      if (!before) {
        console.warn("editTransaction: id transaksi tidak ditemukan");
        return d;
      }

      if (patch.location !== undefined && patch.location !== before.location) {
        console.warn("editTransaction: location tidak bisa diubah setelah dibuat (invariant 18)");
        return d;
      }
      if (
        before.type === "TRANSFER" &&
        patch.transferDirection !== undefined &&
        patch.transferDirection !== before.transferDirection
      ) {
        console.warn("editTransaction: arah transfer tidak bisa diubah setelah dibuat (invariant 18)");
        return d;
      }
      // Invariant 19's second exception: a CABANG-anchored PEMBELIAN's branchId is a plain label
      // the user picked by hand — no salesperson derives it, no invoice number is minted off it,
      // and every branch shares the one Cabang stock pool — so it may be corrected on edit. It
      // must still name a real branch: blanking an already-set one (or naming a missing one) is
      // refused. Every other type keeps the lock (a TRANSFER's branch decides who owes whom).
      const pembelianBranchEditable = before.type === "PEMBELIAN" && before.location === "CABANG";
      if (patch.branchId !== undefined && patch.branchId !== before.branchId) {
        if (!pembelianBranchEditable) {
          console.warn("editTransaction: cabang tidak bisa diubah setelah dibuat (invariant 19)");
          return d;
        }
        if (!(d.branches || []).some((b) => b.id === patch.branchId)) {
          console.warn("editTransaction: cabang pembelian harus cabang yang ada dan tidak boleh dikosongkan");
          return d;
        }
      }

      let cur = d;
      let contactId = patch.contactId ?? null;
      let buyerBlock = patch.buyerBlock ?? before.buyerBlock;
      if (before.type === "TRANSFER") {
        // before.branchId/before.transferDirection are both immutable on this edit path (see
        // this function's own invariant-19 comment above) — safe to resolve off the already-
        // persisted values, the same way createTransaction resolves off the draft.
        const res = resolveTransferContact(cur, before.transferDirection, before.branchId);
        cur = res.data;
        contactId = res.contact ? res.contact.id : null;
        buyerBlock = res.contact
          ? { name: displayContact(res.contact, cur.branches), address: defaultAddressText(res.contact) }
          : null;
      }
      // Write-path guard (see createTransaction's own identical comment, including why an
      // unresolvable contactId is deliberately NOT also rejected here, and its own internal-
      // contact guard note) — `before.location` is used, not a patch field, since location is
      // immutable on edit (invariant 18) regardless. Internal check runs first, same ordering
      // and reasoning as createTransaction's.
      if (before.type !== "TRANSFER" && contactId) {
        const contact = (cur.contacts || []).find((c) => c.id === contactId);
        if (contact && contact.internal) {
          console.warn("editTransaction: kontak internal tidak bisa dipakai untuk transaksi selain Transfer");
          return d;
        }
        if (contact && contact.list !== before.location) {
          console.warn("editTransaction: kontak yang dipilih tidak sesuai dengan lokasi transaksi (Pusat/Cabang)");
          return d;
        }
      }

      let salespersonId = null;
      // Salesperson-correction re-mint feature: `branchId` only ever moves off its frozen
      // `before` value when the picked role's OWN branch differs (`branchChanged`, below);
      // `invoiceNumber` only ever moves when that ALSO comes with a genuine person change
      // (`personChanged && branchChanged`) — the ONE scoped exception to invariant 19 (see this
      // function's own header comment for the full trigger/reasoning, and the bug fix note on why
      // these are two separate booleans, not one). `numberingChange` stays `null` on every other
      // edit, including one that resubmits the SAME salesperson unchanged.
      let branchId = pembelianBranchEditable && patch.branchId !== undefined ? patch.branchId : before.branchId;
      let invoiceNumber = before.invoiceNumber;
      let numberingChange = null;
      if (before.type === "PENJUALAN" || before.type === "TRANSFER") {
        const anchor = txnAnchor(before.type, before.location, before.transferDirection);
        // Invariant 12 (revised): a salesperson is required on EITHER anchor now, so this
        // lookup runs unconditionally for PENJUALAN/TRANSFER — no grandfathering for an old
        // Pusat transaction whose `salespersonId` is still null; editing one now requires
        // picking a real salesperson, same as Cabang has always required.
        const sp = (cur.salespeople || []).find((s) => s.id === patch.salespersonId);
        if (!sp) {
          console.warn(anchor === "PUSAT"
            ? "editTransaction: transaksi pusat wajib memiliki sales"
            : "editTransaction: transaksi cabang wajib memiliki sales");
          return d;
        }
        // Person/Role restructure, Stage 3 batch 2 — the deliberate asymmetry with
        // createTransaction above: an edit that resubmits the SAME salesperson the transaction
        // already had (`sp.id === before.salespersonId`) must succeed even if that role has
        // since been deactivated — the person didn't touch the Sales field, so their save must
        // not be blocked by something that happened to someone else's data afterward, and the
        // transaction's own history must keep resolving/staying editable regardless. Only a
        // genuine REASSIGNMENT to a DIFFERENT role (`sp.id !== before.salespersonId` — deliberately
        // ROLE-based here, unlike the re-mint feature below, which compares PERSON identity; this
        // check is about validating the SPECIFIC role record being submitted, which is always a
        // role-level question regardless of who holds it) requires the newly-picked role to be
        // active, matching createTransaction's "picking for the first time" rule. An
        // old Pusat transaction whose `before.salespersonId` is still `null` (pre-invariant-12
        // data) always falls into this branch too — `sp.id` is never `null`, so editing one is
        // correctly treated as a first-time pick, which must be active.
        if (sp.id !== before.salespersonId && sp.active === false) {
          console.warn("editTransaction: sales yang dipilih sudah tidak aktif");
          return d;
        }
        // Same "the write path checks it too" guard createTransaction applies, and the same
        // asymmetry as the `active` check just above: a genuine REASSIGNMENT to a DIFFERENT
        // role must match the anchor's own kind (a Pusat role for a Pusat anchor, a branch role
        // for a Cabang anchor) — but RESUBMITTING the transaction's own existing salesperson
        // unchanged must keep succeeding even if that role's own kind doesn't match the anchor
        // (reachable in old data, since nothing enforced this on write before now) — the
        // transaction must stay editable regardless of what a mismatch elsewhere in the data
        // looks like, mirroring the `active` exception's own reasoning exactly.
        if (sp.id !== before.salespersonId) {
          const wrongKind = anchor === "PUSAT" ? sp.branchId !== null : sp.branchId === null;
          if (wrongKind) {
            console.warn(anchor === "PUSAT"
              ? "editTransaction: sales yang dipilih bukan peran Pusat"
              : "editTransaction: sales yang dipilih bukan peran cabang");
            return d;
          }
        }
        salespersonId = sp.id;
        if (anchor !== "PUSAT") {
          // The salesperson-correction re-mint feature (invariant 19) is CABANG-only — Pusat
          // has no branch to re-derive and its invoice number is never re-minted on edit at
          // all (decision 4) — so this whole branch must never run for a Pusat-anchored edit.
          //
          // Bug fix ("the re-mint trigger compares roles, not people"): this used to be ONE
          // compound condition, `sp.id !== before.salespersonId && sp.branchId !==
          // before.branchId`, gating BOTH "should branchId re-derive" AND "should we re-mint"
          // together. `sp.id` is a ROLE id, but the invoice counter is keyed on the PERSON's own
          // `code` (`resolveSalespersonCode` below resolves through `personId`, never the role) —
          // so the SAME person moving roles (Budi-at-TMM -> Budi-at-AMIN) tripped `sp.id !==
          // before.salespersonId` (two different role records) and wrongly re-minted a second
          // number under Budi's own counter, abandoning the first for no reason: nothing about
          // who's being billed changed, only which branch the transaction is now anchored to.
          // Split into two independent booleans so each only gates what it actually needs to:
          // `branchChanged` alone re-derives `branchId` (the transaction's stored anchor must
          // always track the picked role's real branch, regardless of whether the PERSON also
          // changed); `personChanged && branchChanged` alone re-mints (the documented invariant-19
          // exception's own wording: "reassign to a genuinely DIFFERENT PERSON whose real branch
          // differs"). A same-branch person reassignment (Albert-at-TMM -> Budi-at-TMM) is
          // UNCHANGED by this fix — `branchChanged` is false there, so neither branchId nor the
          // invoice number move, exactly as documented today; this fix narrows ONLY the
          // false-positive re-mint on a same-person branch move, nothing else.
          const beforeSp = (cur.salespeople || []).find((s) => s.id === before.salespersonId);
          const personChanged = sp.personId !== (beforeSp ? beforeSp.personId : null);
          const branchChanged = sp.branchId !== before.branchId;
          if (branchChanged) {
            branchId = sp.branchId;
          }
          if (personChanged && branchChanged) {
            const minted = mintCabangNumber(cur, resolveSalespersonCode(cur.persons, sp), patch.date);
            cur = { ...cur, txnCounters: minted.updatedCounters };
            invoiceNumber = minted.invoiceNumber;
          }
          // `numberingChange` now fires on ANY real branchId move, not only one that also
          // re-mints — a branch-only move (same person) still changes a field invariant 19
          // otherwise treats as immutable, and that change needs its own visible, logged trace
          // (editLog is append-only; `finalTxn`'s own gate below only writes an entry when
          // SOMETHING changed) just as much as a full re-mint does. `invoiceNumber` here already
          // equals `before.invoiceNumber` unless the block above just reassigned it, so a
          // branch-only move reports the SAME number on both sides — `NumberingChangeBlock`
          // (components/EditLogTimeline.js) renders that case with its own, honest heading rather
          // than implying a number changed that didn't.
          if (branchChanged) {
            numberingChange = {
              before: {
                branchId: before.branchId,
                branchName: resolveBranchName(cur.branches, before.branchId) || "Cabang",
                invoiceNumber: before.invoiceNumber,
              },
              after: {
                branchId: sp.branchId,
                branchName: resolveBranchName(cur.branches, sp.branchId) || "Cabang",
                invoiceNumber,
              },
            };
          }
        }
      }

      // Payment-correction rework: resolve patch.payments -> a stamped afterPayments array
      // BEFORE `after` is built, same "abort before any mutation" placement as every guard
      // above. A row with an `id` must resolve against before.payments (an unresolvable id is
      // a data-integrity anomaly, not a normal user path — abort rather than silently treating
      // it as new/dropped). A row with no `id` is genuinely new and gets stamped here, exactly
      // like createTransaction/recordPayment already do.
      let afterPayments = before.payments || [];
      if (patch.payments !== undefined) {
        const beforeById = new Map((before.payments || []).map((p) => [p.id, p]));
        const now = new Date().toISOString();
        const rows = [];
        let anomaly = false;
        for (const p of patch.payments) {
          if (p.id) {
            const orig = beforeById.get(p.id);
            if (!orig) { anomaly = true; break; }
            // paidAt: falls back to the ORIGINAL value when the patch omits it — unlike the
            // new-row branch below, this is not a "default to now" case; it's "don't silently
            // blank a correction that only touched amount/method."
            // Split fields (two-bucket payment split): spread `...orig` FIRST, then overwrite
            // splitBarang/splitTagihanPotongan ONLY when the incoming patch row explicitly
            // supplies BOTH — this is the literal mechanism behind "correcting an unrelated
            // field never retroactively requires a split." Omitting them leaves whatever
            // split state `orig` already had (present or absent) completely untouched.
            rows.push({
              ...orig, amount: p.amount, method: p.method, methodNote: p.methodNote, paidAt: p.paidAt || orig.paidAt,
              ...(typeof p.splitBarang === "number" && typeof p.splitTagihanPotongan === "number"
                ? { splitBarang: p.splitBarang, splitTagihanPotongan: p.splitTagihanPotongan }
                : {}),
            });
          } else {
            // paidAt: same real defensive-default reasoning as createTransaction's own —
            // the form's combineDateTime never sends blank, but this is the API boundary any
            // caller goes through, so it's what actually implements "default to now."
            rows.push({
              id: generateId(), at: now, paidAt: p.paidAt || now, amount: p.amount, method: p.method, methodNote: p.methodNote,
              ...(typeof p.splitBarang === "number" && typeof p.splitTagihanPotongan === "number"
                ? { splitBarang: p.splitBarang, splitTagihanPotongan: p.splitTagihanPotongan }
                : {}),
            });
          }
        }
        if (anomaly) {
          console.warn("editTransaction: patch.payments berisi id yang tidak dikenal");
          return d;
        }
        afterPayments = rows;
      }

      // Cost items (Brief 1: id-matched-or-abort resolution, same shape as patch.payments
      // above; Brief 2: resolved and validated BEFORE afterTotal, since afterTotal now needs
      // the fully-resolved afterCostItems to compute the real Lunas-mismatch total).
      let afterCostItems = before.costItems || [];
      if (patch.costItems !== undefined) {
        const beforeById = new Map((before.costItems || []).map((c) => [c.id, c]));
        const rows = [];
        let anomaly = false;
        for (const c of patch.costItems) {
          if (c.id) {
            const orig = beforeById.get(c.id);
            if (!orig) { anomaly = true; break; }
            rows.push({ ...orig, description: c.description, amount: c.amount, type: c.type });
          } else {
            rows.push({ id: generateId(), description: c.description, amount: c.amount, type: c.type });
          }
        }
        if (anomaly) {
          console.warn("editTransaction: patch.costItems berisi id yang tidak dikenal");
          return d;
        }
        afterCostItems = rows;
      }
      const costItemsCheck = validateCostItems(afterCostItems, before.type);
      if (!costItemsCheck.ok) {
        console.warn("editTransaction: biaya/tagihan tidak valid — " + costItemsCheck.error);
        return d;
      }

      const afterTotal = txnTotal({ lines: patch.lines, costItems: afterCostItems });
      const totalCheck = validateTxnTotal(afterTotal);
      if (!totalCheck.ok) {
        console.warn("editTransaction: total tidak valid — " + totalCheck.error);
        return d;
      }
      const paymentsResultCheck = patch.isLunas !== undefined
        ? validatePayments(afterPayments, afterTotal, patch.isLunas ? "LUNAS" : "BELUM_LUNAS")
        : validatePaymentSet(afterPayments, afterTotal);
      if (!paymentsResultCheck.ok) {
        console.warn("editTransaction: pembayaran tidak valid — " + paymentsResultCheck.error);
        return d;
      }

      // Two-bucket payment split — only relevant once a live bucket exists on the RESULTING
      // (already-edited) shape. Check 1 (unconditional, every edit, whole resulting array) is
      // the authoritative guard that actually protects money-math integrity — this is what
      // catches an old frozen split now exceeding a shrunk/removed bucket, not just a freshly-
      // typed bad row; TransactionFormModal's own blocking modal is purely a presentation
      // layer in front of THIS guard. Check 2 only runs when the client actually supplied an
      // intent for that bucket (mirrors patch.isLunas's own "only validate when supplied").
      for (const p of afterPayments) {
        if (typeof p.splitBarang === "number" && typeof p.splitTagihanPotongan === "number" && !validateSplitSum(p.amount, p.splitBarang, p.splitTagihanPotongan)) {
          console.warn("editTransaction: pembagian pembayaran tidak sesuai jumlah");
          return d;
        }
      }
      const afterTxnLike = { lines: patch.lines, costItems: afterCostItems, payments: afterPayments };
      const overAlloc = checkBucketOverAllocation(afterTxnLike);
      if (!overAlloc.ok) {
        console.warn("editTransaction: " + overAlloc.violations.map(bucketOverAllocationMessage).join(" "));
        return d;
      }
      if (patch.barangLunas !== undefined) {
        const barangCheck = validateBucketCompleteness("Barang", barangPaid(afterTxnLike), barangOwed(afterTxnLike), !!patch.barangLunas);
        if (!barangCheck.ok) {
          console.warn("editTransaction: " + barangCheck.error);
          return d;
        }
      }
      if (patch.tagihanPotonganLunas !== undefined) {
        const tagihanCheck = validateBucketCompleteness("Tagihan & Potongan", tagihanPotonganPaid(afterTxnLike), tagihanPotonganOwed(afterTxnLike), !!patch.tagihanPotonganLunas);
        if (!tagihanCheck.ok) {
          console.warn("editTransaction: " + tagihanCheck.error);
          return d;
        }
      }

      const after = {
        ...before,
        location: before.location,
        transferDirection: before.transferDirection,
        branchId,
        invoiceNumber,
        contactId,
        salespersonId,
        supplierInvoiceNo: patch.supplierInvoiceNo ?? before.supplierInvoiceNo,
        date: patch.date,
        time: patch.time,
        buyerBlock,
        lines: patch.lines,
        payments: afterPayments,
        costItems: afterCostItems,
        dueDate: patch.dueDate !== undefined ? patch.dueDate : before.dueDate,
        // id / createdAt / returs / editLog / packing: untouched.
        // location / transferDirection: immutable (invariant 18).
        // branchId / invoiceNumber: immutable EXCEPT the one scoped salesperson-correction
        // re-mint exception (invariant 19) — both are `before`'s own frozen values unless the
        // block above reassigned them.
      };
      let stockCur = applyTxnStockDelta(cur, before, -1);
      stockCur = applyTxnStockDelta(stockCur, after, +1);
      // Section E2 (decision "1a"): ONE stock-history entry per item whose stock this edit
      // actually moved, carrying the NET change ("… diubah (5 → 3)", +2) — no longer the old
      // "(dikoreksi)" undo-and-redo pair per line, and NOTHING at all for an edit that leaves
      // stock untouched (due date, payment, address, price). The stock arithmetic itself is
      // unchanged (undo the old effect, apply the new one). One editId links these entries to
      // this edit's edit-log entry.
      const editId = generateId();
      const stockEditEntries = transactionEditEntries(before, after, stockCur.items, editId);
      const { summary, itemChanges } = buildTxnEditDiff(cur, before, after);
      const paymentChanges = buildPaymentDiff(before.payments, after.payments);
      // Brief 3: costChanges diffs before.costItems against the ALREADY-resolved
      // afterCostItems (the id-matched-or-abort loop above) — same shape/reuse pattern as
      // paymentChanges. Folded into the create-entry gate below too, so a cost-items-only
      // edit (no line/payment/header change at all) still produces a real editLog entry
      // instead of silently producing none.
      // Biaya Lain-Lain (2026-10-11): this form is the ONE place a transaction's own costs are
      // changed. `patch.expenses` (the form's full list; omitted = untouched) is synced against
      // the books with `after` already in place, so every linked cost is re-stamped with the
      // transaction's (possibly new) date. Their changes join costChanges as type "biayaLain".
      let expenseData = { ...stockCur, transactions: stockCur.transactions.map((t) => (t.id === id ? after : t)) };
      if (patch.expenses !== undefined) {
        const synced = syncLinkedExpenses(expenseData, after, patch.expenses);
        if (!synced.ok) {
          console.warn("editTransaction: biaya lain-lain tidak valid — " + synced.error);
          return d;
        }
        expenseData = synced.data;
      }
      const costChanges = [
        ...buildCostItemDiff(before.costItems, after.costItems, before.type),
        ...buildCostItemDiff(expensesAsCostItems(cur.expenses, id), expensesAsCostItems(expenseData.expenses, id), before.type),
      ];
      const finalTxn = (summary.length > 0 || itemChanges.length > 0 || paymentChanges.length > 0 || costChanges.length > 0 || numberingChange)
        ? {
            ...after,
            editLog: [
              ...(before.editLog || []),
              {
                at: new Date().toISOString(),
                editId,
                summary,
                itemChanges,
                paymentChanges,
                costChanges,
                numberingChange,
                moneyBefore: txnMoneySnapshot(before),
                moneyAfter: txnMoneySnapshot(after),
              },
            ],
          }
        : after;
      return {
        ...expenseData,
        transactions: expenseData.transactions.map((t) => (t.id === id ? finalTxn : t)),
        history: [...stockCur.history, ...stockEditEntries],
      };
    }, { label: (d) => `Ubah transaksi ${txnName(findTxn(d, id))}`, touches: [touchTxn(id)] });

  /** Delete a transaction: reverses its stock effect and removes it. The invoice counter is
   *  NEVER decremented (PRD invariant 8) — a deleted invoice number leaves a legitimate gap. */
  const deleteTransaction = (id) =>
    update((d) => {
      const txn = (d.transactions || []).find((t) => t.id === id);
      if (!txn) {
        console.warn("deleteTransaction: id transaksi tidak ditemukan");
        return d;
      }
      const reversed = applyTxnStockDelta(d, txn, -1);
      // Section E2: the delete's reversal entries share one editId (a deleted transaction has no
      // edit log left to link to, but the entries still belong together).
      const reversalEntries = transactionEntries(txn, reversed.items, -1, " (dibatalkan)", generateId());
      // Its Biaya Lain-Lain are deleted with it (Chalton, 2026-10-11 — replaces "keep them as
      // orphans"); the delete warning names them first (LinkedExpensesDeleteNote).
      return {
        ...reversed,
        transactions: reversed.transactions.filter((t) => t.id !== id),
        history: [...reversed.history, ...reversalEntries],
        expenses: (reversed.expenses || []).filter((e) => e.txnId !== id),
      };
    }, { label: (d) => `Hapus transaksi ${txnName(findTxn(d, id))}`, touches: [touchTxn(id)] });

  /** Pengepakan (packing, Phase T2b): writes ONLY lines[].collyNo + packing — merges collyNo
   *  onto the STORED lines by index rather than trusting the payload's line objects wholesale,
   *  so this can never be used to smuggle a qty/unitPrice/internalCode change through
   *  (invariant 17: packing is presentation-only, never money/stock). No stock delta, no
   *  history entry, no editLog, no invoice re-mint. Deliberately NOT routed through
   *  editTransaction, which reverses/reapplies stock and appends a diff — wrong shape for a
   *  metadata-only write with no economic effect. */
  const savePacking = (id, { lines, packing }) => {
    update((d) => ({
      ...d,
      transactions: (d.transactions || []).map((t) =>
        t.id === id
          ? {
              ...t,
              lines: t.lines.map((l, i) => ({ ...l, collyNo: lines[i] ? lines[i].collyNo : l.collyNo })),
              packing,
            }
          : t
      ),
    }), {
      label: (d) => `Data colly/surat jalan ${txnName(findTxn(d, id))}`,
      touches: [recordTouch("Data colly/surat jalan transaksi ini", (d) => {
        const t = findTxn(d, id);
        return t ? { packing: t.packing, lines: t.lines.map((l) => [l.internalCode, l.collyNo]) } : null;
      })],
    });
  };

  const ADDRESS_OVERRIDE_FIELDS = {
    invoicePusat: ["pusatAddressLines", "buyerAddress"],
    invoiceCabang: ["buyerAddress"],
    suratJalanPusat: ["buyerAddress"],
    suratJalanCabang: ["buyerAddress"],
  };

  /** Address overrides (per-document, per-transaction print-only correction — same
   *  invariant-17 class as savePacking): writes ONLY txn.addressOverrides[docType], allowlisted
   *  per docType — never trusts the payload wholesale. No stock delta, no history entry, no
   *  editLog, no invoice re-mint; never touches the Contact record or company settings.
   *  Deliberately NOT routed through editTransaction, for the same reason savePacking isn't. */
  const saveAddressOverride = (id, docType, overrides) => {
    const allowedFields = ADDRESS_OVERRIDE_FIELDS[docType];
    if (!allowedFields) {
      console.warn("saveAddressOverride: docType tidak dikenal — " + docType);
      return;
    }
    const picked = {};
    for (const f of allowedFields) if (f in overrides) picked[f] = overrides[f];
    update((d) => ({
      ...d,
      transactions: (d.transactions || []).map((t) =>
        t.id === id
          ? { ...t, addressOverrides: { ...(t.addressOverrides || {}), [docType]: picked } }
          : t
      ),
    }), {
      label: (d) => `Alamat cetak ${txnName(findTxn(d, id))}`,
      touches: [recordTouch("Alamat cetak transaksi ini", (d) => {
        const t = findTxn(d, id);
        return t ? ((t.addressOverrides || {})[docType] || null) : null;
      })],
    });
  };

  /** Record one or more new payments against an existing transaction (Bayar Tagihan) —
   *  appends to `txn.payments`, stamping `id`/`at` server-side exactly like `createTransaction`
   *  does; never replaces existing rows. Deliberately NOT routed through `editTransaction`
   *  (no stock/line effect, no interaction with invariants 18/19 — `location`/
   *  `transferDirection`/`branchId` are never in this function's patch surface at all) — same
   *  class of narrow, allowlisted write as `savePacking`/`saveAddressOverride`. Validates the
   *  FULL resulting array (existing + new) via `validatePaymentSet` — no Lunas/Belum-Lunas
   *  `intent` to satisfy here (there isn't one once a transaction already exists; status is
   *  simply derived fresh from `txnStatus()` afterward) — aborts with no write if the sum would
   *  exceed the total. This also means a transaction already at Sisa 0 naturally rejects any
   *  new payment with no extra guard needed: `sum <= total` already leaves no room. Appends an
   *  editLog entry (`paymentChanges`, `kind: "payment_added"`, one per new row) with a
   *  moneyBefore/moneyAfter snapshot — total never moves, only sisa/dibayar.
   *
   *  Payment-correction rework: `editTransaction` is now the OTHER place `payments` can
   *  change (a full row-by-row correct/add/remove via its own `patch.payments`, diffed by
   *  `buildPaymentDiff`) — this function stays scoped to Bayar Tagihan's own quick add-only
   *  flow specifically because it never touches lines/header fields; routing it through
   *  `editTransaction` would force building an irrelevant full patch for a "just add money"
   *  action. The two write paths never conflict: `buildPaymentDiff` matches by `id`, so a row
   *  this function stamps is just as correctable/removable later via `editTransaction` as one
   *  stamped at creation. */
  const recordPayment = (txnId, newPaymentRows) =>
    update((d) => {
      const before = (d.transactions || []).find((t) => t.id === txnId);
      if (!before) {
        console.warn("recordPayment: id transaksi tidak ditemukan");
        return d;
      }
      if (!newPaymentRows || newPaymentRows.length === 0) {
        console.warn("recordPayment: tidak ada baris pembayaran untuk disimpan");
        return d;
      }

      const total = txnTotal(before);
      const at = new Date().toISOString();
      // Two-bucket payment split: per-row shape check on every new row up front — this modal
      // never edits lines/cost-items, so Check 1 below can ONLY ever be caused by a row being
      // added right now (no old/stale data scenario is structurally possible here). No Check 2
      // in this write path — Bayar Tagihan has no Lunas/Belum-Lunas toggle of its own; only the
      // split-cap rule (Check 1) applies to an add-only flow.
      for (const p of newPaymentRows) {
        if (typeof p.splitBarang === "number" && typeof p.splitTagihanPotongan === "number" && !validateSplitSum(p.amount, p.splitBarang, p.splitTagihanPotongan)) {
          console.warn("recordPayment: pembagian pembayaran tidak sesuai jumlah");
          return d; // abort — no mutation
        }
      }
      const stamped = newPaymentRows.map((p) => ({
        id: generateId(), at,
        // paidAt: same real defensive default as createTransaction/editTransaction's own —
        // Bayar Tagihan's combineDateTime never sends blank, but this is the API boundary any
        // caller goes through, so it's what actually implements "default to now."
        paidAt: p.paidAt || at,
        amount: p.amount, method: p.method, methodNote: p.methodNote,
        ...(typeof p.splitBarang === "number" && typeof p.splitTagihanPotongan === "number"
          ? { splitBarang: p.splitBarang, splitTagihanPotongan: p.splitTagihanPotongan }
          : {}),
      }));
      const resultPayments = [...(before.payments || []), ...stamped];
      const check = validatePaymentSet(resultPayments, total);
      if (!check.ok) {
        console.warn("recordPayment: pembayaran tidak valid — " + check.error);
        return d; // abort — no mutation
      }
      const overAlloc = checkBucketOverAllocation({ ...before, payments: resultPayments });
      if (!overAlloc.ok) {
        console.warn("recordPayment: " + overAlloc.violations.map(bucketOverAllocationMessage).join(" "));
        return d; // abort — no mutation
      }

      const after = { ...before, payments: resultPayments };
      const paymentChanges = stamped.map((p) => {
        const fields = [`Rp 0 → Rp ${fmtQty(p.amount)}`];
        if (typeof p.splitBarang === "number" && typeof p.splitTagihanPotongan === "number") {
          fields.push(`Untuk Barang: Rp 0 → Rp ${fmtQty(p.splitBarang)}`);
          fields.push(`Untuk Tagihan & Potongan: Rp 0 → Rp ${fmtQty(p.splitTagihanPotongan)}`);
        }
        return { kind: "payment_added", label: paymentMethodText(p), fields };
      });
      const finalTxn = {
        ...after,
        editLog: [
          ...(before.editLog || []),
          {
            at,
            summary: [],
            itemChanges: [],
            paymentChanges,
            moneyBefore: txnMoneySnapshot(before),
            moneyAfter: txnMoneySnapshot(after),
          },
        ],
      };
      return {
        ...d,
        transactions: (d.transactions || []).map((t) => (t.id === txnId ? finalTxn : t)),
      };
    }, { label: (d) => `Pembayaran untuk ${txnName(findTxn(d, txnId))}` });


  // ── Item CRUD ─────────────────────────────────────────────────────────────────
  const saveItem = (item) => {
    let saved = false;
    update((d) => {
      const isEdit = item.internalCode && d.items.some((it) => it.internalCode === item.internalCode);
      // Strip the transport-only fields before building the persisted item.
      const { newCategoryCode, siblingCodes, openingContactId, ...itemFields } = item;
      const withFitment = { ...itemFields, fitment: parseFitment(item.fitment) };
      let cur, code;
      if (isEdit) {
        saved = true;
        code = item.internalCode;
        cur = {
          ...d,
          // internalCode is always forced back to the stored item's own value — mirrors
          // ItemFormModal's category force-back pattern — so a payload can never rewrite it,
          // even though today's lookup above already guarantees it/item agree by construction.
          // Stock is always kept from the STORED item, never taken from the form: stock only
          // changes through logged paths (root CLAUDE.md), and on the shared books the form's
          // numbers may be older than a sale someone else just made (multi-device sync, 5a).
          items: d.items.map((it) =>
            it.internalCode === item.internalCode
              ? { ...it, ...withFitment, internalCode: it.internalCode, stokPusat: it.stokPusat, stokCabang: it.stokCabang }
              : it
          ),
        };
      } else {
        const { categories, internalCode, error } = resolveItemCreate(d, item.category, { explicitCode: newCategoryCode });
        if (error) {
          // Safety net: live picker validation prevents this in normal use.
          console.warn("saveItem: resolveItemCreate error —", error);
          return d; // abort; modal stays open, no link writes
        }
        saved = true;
        code = internalCode;
        const normalized = { ...withFitment, internalCode };
        cur = {
          ...d,
          categories,
          items: [...d.items, normalized],
          history: [...d.history, ...creationEntries(normalized, undefined, openingContactId || null)],
        };
      }
      // Reconcile sibling links against the staged codes: link every staged-not-current pair,
      // unlink every current-not-staged pair. Staged codes for items deleted since staging are
      // dropped rather than persisted as links to nothing.
      const staged = (siblingCodes || []).filter((c) => cur.items.some((it) => it.internalCode === c));
      const current = siblingsOf(cur, code);
      for (const c of staged) {
        if (!current.includes(c)) cur = linkSiblings(cur, code, c);
      }
      for (const c of current) {
        if (!staged.includes(c)) cur = unlinkSiblings(cur, code, c);
      }
      return cur;
    }, item.internalCode
      ? { label: `Barang ${item.internalCode}`, touches: [touchItemDetails(item.internalCode)] }
      : { label: `Barang baru "${(item.name || "").trim()}"`, newItemCodes: "notify" });
    if (saved) setItemModal(null);
  };

  /** Delete an item. Removes the item and its sibling links — history, recipes, and
   *  transformations are kept (recipes referencing it render "tidak tersedia"). */
  const deleteItem = (code) =>
    update((d) => removeItemLinks(
      { ...d, items: d.items.filter((it) => it.internalCode !== code) }, code
    ), { label: `Hapus barang ${code}`, touches: [touchItem(code)] });

  // Pure: resolve a pending output item's code+category without persisting. pendingItems and
  // pendingCategories are in-flight creations so sequential codes and new-category dedup stay
  // consistent across multiple calls before any commit. explicitCode is the user's chosen code
  // for a brand-new category; ignored when the category already exists in synthetic.
  const previewItemCreate = (categoryName, pendingItems = [], pendingCategories = [], explicitCode) => {
    const synthetic = {
      ...dataRef.current,
      items: [...dataRef.current.items, ...pendingItems],
      categories: [...(dataRef.current.categories || []), ...pendingCategories],
    };
    const { categories, internalCode, error } = resolveItemCreate(synthetic, categoryName, { explicitCode });
    if (error) {
      console.warn("previewItemCreate: resolveItemCreate error —", error);
    }
    const known = new Set([
      ...(dataRef.current.categories || []).map((c) => c.code),
      ...pendingCategories.map((c) => c.code),
    ]);
    const newCategory = (categories || synthetic.categories).find((c) => !known.has(c.code)) || null;
    return { internalCode, newCategory };
  };

  // Commit pending inline items atomically at confirm time. Resolves each against the
  // accumulating cur so codes stay consistent even when multiple items share a new category.
  // p.newCategory carries the user's chosen {name, code} for a new category; it's passed as
  // explicitCode so the committed category uses the user's code. On the 2nd item in the same
  // new category, that category already exists in cur (created by the 1st iteration) so
  // resolveCategoryForCreate uses it directly and ignores explicitCode — one category, user's
  // code.
  //
  // Item-code-reuse fix: mints via ONE upfront maxCodeNumbersByPrefix(d) scan (covering items,
  // transaction lines, history entries, recipe lines, and transformation lines across the WHOLE
  // batch) plus a local per-prefix counter — mirroring importCommit.js's buildCommit — rather
  // than calling resolveItemCreate (a full re-scan) once per pending item. cur.transactions/
  // history/recipes/transformations never change across this loop (only items/categories do),
  // so re-scanning them per item would be pure waste at any pending-item count.
  const commitPendingItems = (pendingItems = []) => {
    if (!pendingItems.length) return [];
    // Collected instead of only console.warn'd, so a caller can surface which pending items (if
    // any) failed to mint rather than them silently vanishing from the batch.
    const skipped = [];
    update((d) => {
      let cur = d;
      const maxByPrefix = maxCodeNumbersByPrefix(d); // ONE scan for the whole batch
      const counters = new Map(); // prefix -> next number to mint
      for (const p of pendingItems) {
        const explicitCode = (p.newCategory && p.newCategory.name === p.category) ? p.newCategory.code : undefined;
        const { categories, prefix, error } = resolveCategoryForCreate(cur, p.category, { explicitCode });
        if (error) { skipped.push({ name: p.name, category: p.category, error }); continue; }
        if (!counters.has(prefix)) counters.set(prefix, (maxByPrefix.get(prefix) || 0) + 1);
        const n = counters.get(prefix);
        counters.set(prefix, n + 1);
        const internalCode = `${prefix}-${String(n).padStart(5, "0")}`;
        const item = {
          internalCode, marketCodes: [], name: (p.name || "").trim(),
          fitment: [], category: (p.category || "").trim(), tipe: null,
          stokPusat: 0, stokCabang: 0, isSet: !!p.isSet, notes: "", minStock: null,
        };
        cur = {
          ...cur,
          categories,
          items: [...cur.items, item],
          history: cur.history, // no creation entry; transform entry is this item's first record
        };
      }
      return cur;
    }, { label: "Barang baru dari Transformasi", newItemCodes: "same" });
    return skipped;
  };

  // ── Penyesuaian (direct bucket adjustment, logged to riwayat) ──────────────────
  const applyAdjustment = ({ bucket, newValue, delta, note, contactId }) => {
    const item = adjustItem;
    const field = bucket === "CABANG" ? "stokCabang" : "stokPusat";
    update((d) => ({
      ...d,
      items: d.items.map((it) =>
        it.internalCode === item.internalCode ? { ...it, [field]: newValue } : it
      ),
      history: [
        ...d.history,
        {
          id: generateId(),
          itemCode: item.internalCode,
          date: new Date().toISOString(),
          kind: "PENYESUAIAN",
          bucket,
          delta,
          balanceAfter: newValue,
          ref: null,
          note: note || "",
          contactId: contactId || null,
        },
      ],
    }), {
      label: `Penyesuaian stok ${item.internalCode} (${bucket === "CABANG" ? "Cabang" : "Pusat"})`,
      touches: [{
        what: "Stok barang ini",
        get: (d) => { const it = (d.items || []).find((x) => x.internalCode === item.internalCode); return it ? it[field] : null; },
        describe: (was, now) => `stok ${item.internalCode} (${bucket === "CABANG" ? "Cabang" : "Pusat"}) berubah dari ${was} menjadi ${now} di perangkat lain saat Anda sedang menyesuaikannya`,
      }],
    });
    setAdjustItem(null);
  };

  // ── Transformation engine wiring ───────────────────────────────────────────────
  /** Runs the pure engine against the latest state; commits only on success. Returns {ok,error?}.
   *  bornCodes: internalCodes created inline this run — their TRANSFORMASI entries get a note
   *  appended so the riwayat row reads "Transformasi — Rakit (Pusat) · Barang baru dibuat (Rakit)".
   *  Input lines carrying substituteFor (Transformasi's sibling-substitution hint) similarly get
   *  " · Substitusi untuk <original code> (stok kurang)" appended to their TRANSFORMASI entry. */
  const runTransformation = (txn, bornCodes = []) => {
    // Checked once against what the screen shows, so the caller gets the engine's own error.
    const check = applyTransformation(dataRef.current, txn);
    if (!check.ok) return check;
    // Multi-device sync (5a): the new stock is computed INSIDE the change, from the books it is
    // applied to — so if it has to be re-applied on top of someone else's newer books, its
    // deltas land on the newest stock instead of overwriting their stock changes with numbers
    // computed from an older copy. The transformation keeps one fixed id either way.
    const fixedTxn = { ...txn, id: txn.id || check.transformation.id };
    const bornSet = new Set(bornCodes);
    const typeLabel = txn.type === "TEARDOWN" ? "Urai" : "Rakit";
    const bornNote = `Barang baru dibuat (${typeLabel})`;
    const substitutions = (txn.inputs || []).filter((l) => l.substituteFor);
    const codes = [...new Set([...(txn.inputs || []), ...(txn.outputs || [])].map((l) => l.internalCode))];
    let result = check;
    update((d) => {
      const res = applyTransformation(d, fixedTxn);
      if (!res.ok) return d;
      result = res;
      // Notes go ONLY on this run's own new entries (ref === this transformation's id). This
      // used to map over the WHOLE history, so a substitute item's notes were also appended to
      // its OLDER Transformasi entries — rewriting past riwayat, which is append-only.
      const history = (bornSet.size === 0 && substitutions.length === 0) ? res.history : res.history.map((e) => {
        if (e.kind !== "TRANSFORMASI" || e.ref !== res.transformation.id) return e;
        let note = e.note;
        if (bornSet.has(e.itemCode)) note = note ? `${note} · ${bornNote}` : bornNote;
        const sub = substitutions.find((l) => l.internalCode === e.itemCode && e.bucket === txn.bucket);
        if (sub) {
          const subNote = `Substitusi untuk ${sub.substituteFor} (stok kurang)`;
          note = note ? `${note} · ${subNote}` : subNote;
        }
        return note === e.note ? e : { ...e, note };
      });
      return { ...d, items: res.items, transformations: res.transformations, history };
    }, {
      label: `Transformasi (${typeLabel})`,
      // The items it uses must still be the SAME items — a code taken by someone else's new item
      // meanwhile must never receive this transformation's stock.
      touches: [recordTouch("Barang dalam transformasi ini", (d) => codes.map((c) => {
        const it = (d.items || []).find((x) => x.internalCode === c);
        return it ? { code: c, name: it.name, category: it.category, isSet: !!it.isSet } : { code: c, missing: true };
      }))],
    });
    return result;
  };

  /** "Simpan sebagai resep" — add a named recipe (own collection) for a target SET. */
  const addRecipe = (recipe) =>
    update((d) => ({
      ...d,
      recipes: [...d.recipes, {
        id: generateId(),
        ...recipe,
        inputs: stampNames(d.items, recipe.inputs),
        outputs: stampNames(d.items, recipe.outputs),
      }],
    }), { label: `Simpan resep "${(recipe && recipe.name) || ""}"` });

  /** Delete a recipe. Removes only that recipe — no items, stock, history, or transformations touched. */
  const deleteRecipe = (id) =>
    update((d) => ({ ...d, recipes: d.recipes.filter((r) => r.id !== id) }),
      { label: "Hapus resep", touches: [touchRecipe(id)] });

  /** Update a recipe in place by id (name/note/lines). Touches nothing else. */
  const updateRecipe = (id, patch) =>
    update((d) => {
      const p = { ...patch };
      if (p.inputs) p.inputs = stampNames(d.items, p.inputs);
      if (p.outputs) p.outputs = stampNames(d.items, p.outputs);
      return { ...d, recipes: d.recipes.map((r) => (r.id === id ? { ...r, ...p } : r)) };
    }, { label: "Ubah resep", touches: [touchRecipe(id)] });

  const setLowStockThreshold = (value) =>
    update((d) => {
      const next = Math.max(0, Number(value) || 0);
      if ((d.settings || {}).lowStockThreshold === next) return d;
      return { ...d, settings: { ...(d.settings || {}), lowStockThreshold: next } };
    }, { label: "Stok minimum umum", touches: [touchSetting("lowStockThreshold", "Stok minimum umum")] });

  // Tempo Pembayaran redesign: the day-count TransactionFormModal defaults a NEW Belum Lunas
  // transaction's due date to — replaces the old hardcoded `14`. Same small single-purpose
  // numeric-setting-setter shape as setLowStockThreshold above, kept separate from
  // updateCompanySettings below (that one replaces `company` wholesale; this is an unrelated
  // shop-wide number, not letterhead text).
  const setDefaultDueDays = (value) =>
    update((d) => {
      const next = Math.max(1, Math.round(Number(value)) || 14);
      if ((d.settings || {}).defaultDueDays === next) return d;
      return { ...d, settings: { ...(d.settings || {}), defaultDueDays: next } };
    }, { label: "Pengaturan: Default Jatuh Tempo", touches: [touchSetting("defaultDueDays", "Default Jatuh Tempo")] });

  // Pengaturan (Phase T2-settings): the printed letterhead, editable so a typo/wrong NPWP
  // digit never needs a code session. `company` is always the FULL {name, addressLines, npwp}
  // object PengaturanModal builds — replaces wholesale, no partial merge.
  // Multi-branch cabang feature: PengaturanModal no longer edits `cabangName` (superseded by
  // the real `data.branches` managed list) — this reverts to a single argument;
  // `settings.cabangName`'s stored value is simply never written again (vestigial, not
  // migrated away — see storage.js's own comment). Branch management (Kelola Cabang) has since
  // moved from a Penjualan.js page-header button INTO Pengaturan itself (see the
  // <PengaturanModal> render below) — Pengaturan is now the single home for app-level settings.
  const updateCompanySettings = (company) =>
    update((d) => {
      if (sameValue((d.settings || {}).company, company)) return d;
      return { ...d, settings: { ...(d.settings || {}), company } };
    }, { label: "Pengaturan: data perusahaan", touches: [touchSetting("company", "Data perusahaan")] });

  // Pusat's own bank account (Pengaturan-as-single-home-for-settings pass): free text, lives on
  // `data.settings.bankAccount` — a sibling of `company`, not nested inside it, alongside
  // `defaultDueDays` above. Pusat has no `data.branches` record (branchId: null everywhere in
  // this app), so this is the ONE place its account is stored; a Cabang branch's own account
  // lives on its own branch record instead (utils/branches.js's addBranch/updateBranch).
  // `utils/branches.js`'s `bankAccountFor(data, branchId)` is the one function every future
  // reader should use to resolve either location's account — never read this key directly.
  // Never validated/reformatted — stored exactly as typed, same as `company.npwp`.
  const updatePusatBankAccount = (bankAccount) =>
    update((d) => {
      if (((d.settings || {}).bankAccount || "") === (bankAccount || "")) return d;
      return { ...d, settings: { ...(d.settings || {}), bankAccount } };
    }, { label: "Pengaturan: rekening bank Pusat", touches: [touchSetting("bankAccount", "Rekening bank Pusat")] });

  // ── Catalog import (one-time Excel import via ImportWizard) ────────────────────
  /** Commit a buildCommit result in ONE update().
   *
   *  IMPORTANT asymmetry in importCommit's contract (verified directly against its code, not
   *  assumed): `result.categories` / `result.cars` / `result.history` are each already built as
   *  `[...existingData.X, ...newX]` — full replacements. `result.items` is NOT — it is ONLY the
   *  newly-minted items (`(parsed.items||[]).map(...)`, no spread of existingData.items). So
   *  items must be explicitly concatenated onto a BASE derived from the CURRENT `d`, while the
   *  other three are safe to replace outright. `base` is `d` itself normally, the 7 seed items
   *  (+ their history) stripped from `d` when "remove seed" was checked, or a fully `wipedData`
   *  reset when "wipe all" was checked — the SAME three cases the wizard's own `effectiveData`
   *  derives, re-applied here against the live `d` (rather than trusting the wizard's snapshot)
   *  so the merge stays correct even if it were stale — though in this single-page-flow app
   *  nothing else can mutate `data` while the wizard is open. When wiping, `transformations`/
   *  `recipes`/`siblingLinks`/`managedLists`/`settings` all come from `base` (i.e. emptied) —
   *  `buildCommit`'s result never touches those fields at all. */
  const commitImport = (result, { removeSeed, wipeAll } = {}) => {
    update((d) => {
      let base = d;
      if (wipeAll) {
        base = wipedData(d);
      } else if (removeSeed) {
        const seedSet = new Set(SEED_CODES);
        base = {
          ...d,
          items: d.items.filter((it) => !seedSet.has(it.internalCode)),
          history: d.history.filter((h) => !seedSet.has(h.itemCode)),
        };
        base = SEED_CODES.reduce((acc, code) => removeItemLinks(acc, code), base);
      }
      return {
        ...base,
        items: [...base.items, ...result.items],
        categories: result.categories,
        cars: result.cars,
        history: result.history,
      };
    }, { label: "Impor barang dari Excel", replay: false });
    const note = wipeAll ? " (semua data lama dihapus)" : (removeSeed ? " (7 barang contoh dihapus)" : "");
    setImportOkMsg(
      `Impor selesai: ${result.stats.itemsMinted} barang, ${result.stats.categoriesCreated} kategori baru, ` +
      `${result.stats.carsCreated} mobil baru${note}.`
    );
  };

  // ── Contacts import (contacts Excel import via ImportKontakWizard, batch 5b) ───────────────────
  /** Unlike `commitImport` above, `ImportKontakWizard` hands back the FULL new `data` object
   *  already, not a "newly minted only" partial result — `resolveSalesPersonMapping`/
   *  `commitContactImport` both work by threading the whole accumulating `data` through
   *  (`addPerson`/`addContact`, the same pure primitives every other write path in this app
   *  uses), so there is no asymmetry to reconcile here: one plain `update()`. */
  const commitKontakImport = (finalData) => {
    update(() => finalData, { label: "Impor kontak dari Excel", replay: false });
  };

  // ── Navigation helpers ──────────────────────────────────────────────────────────
  const openItem = (code) => setSelectedCode(code);
  const goNav = (key) => {
    if (key === "transformasi") setTransformTarget(null);
    setKontakJumpTo(null);
    setImportOkMsg("");
    setPage(key);
  };
  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      saveSidebarCollapsed(next);
      return next;
    });
  };

  const saveLabel = {
    ok: "Tersimpan", pending: "Menyimpan…", err: "Gagal simpan!",
    offline: "Offline — mencoba lagi…",
    conflict: "Belum tersimpan", outdated: "Belum tersimpan", rejected: "Gagal simpan!",
  }[saveState] || "Gagal simpan!";
  const saveDotClass = saveState === "ok" ? "dot--ok" : saveState === "pending" || saveState === "offline" ? "dot--pending" : "dot--err";

  // A deliberate reload (from a sync banner) must not trip the "unsaved changes" guard — the
  // banner itself already states what's unsaved.
  const reloadPage = () => { allowUnloadRef.current = true; window.location.reload(); };

  // What the stopped-sync banner says. Each states plainly what is NOT saved and what to do.
  const syncBannerText = !syncProblem ? null
    : syncProblem.state === "conflict"
      ? `${syncProblem.updatedBy ? `"${syncProblem.updatedBy}"` : "Perangkat lain"} baru saja menyimpan perubahan ke data bersama. ` +
        "Perubahan terakhir Anda di perangkat ini BELUM tersimpan. Muat ulang halaman untuk mengambil data terbaru, lalu ulangi perubahan Anda."
      : syncProblem.state === "outdated"
        ? "Data bersama sudah disimpan oleh versi aplikasi yang lebih baru. Perubahan Anda BELUM tersimpan. Muat ulang halaman untuk memakai versi terbaru, lalu ulangi perubahan Anda."
        : `${syncProblem.message} Perubahan terakhir Anda BELUM tersimpan.`;
  const navActive = page;

  return (
    <ExpensesContext.Provider value={{ expenses: data.expenses || [] }}>
    <div className="app-shell">
      <aside className={`sidebar ${sidebarCollapsed ? "sidebar--collapsed" : ""}`}>
        <div className="sidebar__logo">
          <div className="sidebar__title">Buku Kas IJM</div>
        </div>
        <nav className="sidebar__nav">
          {NAV.map((n) => {
            const Icon = NAV_ICONS[n.key];
            return (
              <button
                key={n.key}
                className={`nav-item ${navActive === n.key ? "nav-item--active" : ""}`}
                onClick={() => goNav(n.key)}
                aria-label={n.label}
                title={n.label}
              >
                <Icon />
                <span className="nav-item__label">{n.label}</span>
              </button>
            );
          })}
          {/* Pengaturan (T2-settings) opens a small modal, not a nav page — never gets
              nav-item--active, since it doesn't correspond to any `page` value. */}
          <button
            className="nav-item"
            onClick={() => setPengaturanOpen(true)}
            aria-label="Pengaturan"
            title="Pengaturan"
          >
            <PengaturanIcon />
            <span className="nav-item__label">Pengaturan</span>
          </button>
        </nav>
        <button
          type="button"
          className="sidebar__collapse-btn"
          onClick={toggleSidebarCollapsed}
          aria-label={sidebarCollapsed ? "Perluas sidebar" : "Ciutkan sidebar"}
          title={sidebarCollapsed ? "Perluas" : "Ciutkan"}
        >
          <CollapseIcon className={sidebarCollapsed ? "sidebar__collapse-icon sidebar__collapse-icon--flipped" : "sidebar__collapse-icon"} />
          <span className="nav-item__label">Ciutkan</span>
        </button>
        <div className="sidebar__foot">
          <span className="save-state">
            <span className={`dot ${saveDotClass}`} />
            <span className="save-state__label">{saveLabel}</span>
          </span>
          {saveQueue && device.label && <div className="save-state__device" title="Nama perangkat ini">{device.label}</div>}
        </div>
      </aside>

      <main className="main-content">
        {syncNotice && <SyncNotice notice={syncNotice} onClose={() => setSyncNotice(null)} />}
        {syncBannerText && (
          <div className="alert alert--error sync-banner" role="alert">
            <div>{syncBannerText}</div>
            <button type="button" className="btn" onClick={reloadPage}>Muat ulang halaman</button>
          </div>
        )}
        {page === "daftar" && (
          <>
            {importOkMsg && <div className="alert alert--ok">{importOkMsg}</div>}
            <DaftarBarang
              items={data.items}
              onOpenItem={openItem}
              onAddItem={() => setItemModal({ create: true })}
              onAddSet={() => setItemModal({ create: true, isSet: true })}
              onEdit={(it) => setItemModal({ initial: it })}
              onDelete={(it) => setDeleteTarget(it)}
              lowStockThreshold={data.settings?.lowStockThreshold ?? 0}
              onSetThreshold={setLowStockThreshold}
              categories={data.categories}
              onManageCategories={() => setKelolaKategori(true)}
              categoryVisits={data.categoryVisits || {}}
              onCategoryVisit={recordCategoryVisit}
              onImport={() => setPage("impor")}
            />
          </>
        )}

        {page === "impor" && (
          <ImportWizard
            data={data}
            onCommit={commitImport}
            onDone={() => setPage("daftar")}
          />
        )}

        {page === "transformasi" && (
          <Transformasi
            items={data.items}
            recipes={data.recipes}
            initialTargetCode={transformTarget}
            onSubmit={runTransformation}
            onSaveRecipe={addRecipe}
            onPreviewItemCreate={previewItemCreate}
            onCommitPendingItems={commitPendingItems}
            categories={data.categories}
            siblingLinks={data.siblingLinks || []}
            onDone={() => setPage("daftar")}
            onViewHistory={() => { setRiwayatFocusId(null); setPage("riwayat"); }}
          />
        )}

        {page === "riwayat" && (
          <RiwayatTransformasi
            transformations={data.transformations || []}
            history={data.history || []}
            items={data.items}
            recipes={data.recipes || []}
            focusId={riwayatFocusId}
            key={riwayatFocusId || "riwayat"}
            onBack={() => { setRiwayatFocusId(null); setPage("transformasi"); }}
          />
        )}

        {page === "resep" && (
          <RecipeManager
            recipes={data.recipes}
            items={data.items}
            onOpenRecipe={setSelectedRecipeId}
            onEditRecipe={setEditRecipeTarget}
            onDeleteRecipe={setDeleteRecipeTarget}
          />
        )}

        {page === "mobil" && (
          <MobilPage
            cars={data.cars}
            items={data.items}
            onOpenCar={setSelectedCarKey}
            onMergeCar={handleMergeCar}
            onRenameCar={handleRenameCar}
            onDeleteCar={handleDeleteCar}
            onCreateCar={createCar}
          />
        )}

        {page === "penjualan" && (
          <Penjualan
            transactions={data.transactions || []}
            items={data.items}
            contacts={data.contacts || []}
            salespeople={data.salespeople || []}
            persons={data.persons || []}
            branches={data.branches || []}
            siblingLinks={data.siblingLinks || []}
            onCreateTransaction={createTransaction}
            onUpdateTransaction={editTransaction}
            onDeleteTransaction={deleteTransaction}
            onSavePacking={savePacking}
            onSaveAddressOverride={saveAddressOverride}
            onRecordPayment={recordPayment}
            onUpdateSalesperson={handleUpdateSalesperson}
            onDeleteSalesperson={handleDeleteSalesperson}
            onMoveSalesperson={handleMoveSalesperson}
            onCreateSalespersonRole={createSalespersonRole}
            onUpdatePerson={handleUpdatePerson}
            onDeletePerson={handleDeletePerson}
            onCreateContact={createContact}
            onCreateContactFull={createContactFull}
            onOpenItem={(code) => { setPage("daftar"); setSelectedCode(code); }}
            company={data.settings?.company}
            defaultDueDays={data.settings?.defaultDueDays ?? 14}
          />
        )}

        {page === "pembelian" && (
          <Pembelian
            transactions={data.transactions || []}
            items={data.items}
            contacts={data.contacts || []}
            persons={data.persons || []}
            branches={data.branches || []}
            siblingLinks={data.siblingLinks || []}
            salespeople={data.salespeople || []}
            onCreateTransaction={createTransaction}
            onUpdateTransaction={editTransaction}
            onDeleteTransaction={deleteTransaction}
            onRecordPayment={recordPayment}
            onCreateSalespersonRole={createSalespersonRole}
            onCreateContact={createContact}
            onCreateContactFull={createContactFull}
            onOpenItem={(code) => { setPage("daftar"); setSelectedCode(code); }}
            defaultDueDays={data.settings?.defaultDueDays ?? 14}
          />
        )}

        {page === "pengeluaran" && (
          <Pengeluaran
            expenses={data.expenses || []}
            transactions={data.transactions || []}
            branches={data.branches || []}
            items={data.items}
            contacts={data.contacts || []}
            salespeople={data.salespeople || []}
            persons={data.persons || []}
            onSave={handleSaveExpense}
            onDelete={handleDeleteExpense}
          />
        )}

        {page === "piutangutang" && (
          <PiutangUtang
            transactions={data.transactions || []}
            items={data.items}
            contacts={data.contacts || []}
            salespeople={data.salespeople || []}
            persons={data.persons || []}
            branches={data.branches || []}
            siblingLinks={data.siblingLinks || []}
            onUpdateTransaction={editTransaction}
            onDeleteTransaction={deleteTransaction}
            onSavePacking={savePacking}
            onSaveAddressOverride={saveAddressOverride}
            onRecordPayment={recordPayment}
            onCreateContact={createContact}
            onCreateContactFull={createContactFull}
            onCreateSalespersonRole={createSalespersonRole}
            onOpenItem={(code) => { setPage("daftar"); setSelectedCode(code); }}
            company={data.settings?.company}
            defaultDueDays={data.settings?.defaultDueDays ?? 14}
          />
        )}

        {page === "kontak" && (
          <KontakPage
            contacts={data.contacts || []}
            items={data.items}
            history={data.history}
            transactions={data.transactions || []}
            persons={data.persons || []}
            salespeople={data.salespeople || []}
            branches={data.branches || []}
            businesses={data.businesses || []}
            onAdd={handleAddContact}
            onUpdate={handleUpdateContact}
            onDelete={handleDeleteContact}
            onLinkContacts={handleLinkContacts}
            onImportKontak={() => setPage("impor-kontak")}
            onOpenBisnis={() => setPage("bisnis")}
            jumpTo={kontakJumpTo}
          />
        )}

        {page === "impor-kontak" && (
          <ImportKontakWizard
            data={data}
            onCommit={commitKontakImport}
            onDone={() => setPage("kontak")}
          />
        )}

        {page === "bisnis" && (
          <BisnisPage
            businesses={data.businesses || []}
            contacts={data.contacts || []}
            transactions={data.transactions || []}
            onOpenContact={(c) => { setKontakJumpTo({ contactId: c.id, list: c.list }); setPage("kontak"); }}
            onUpdate={handleUpdateContact}
            onLinkContacts={handleLinkContacts}
            onDone={() => setPage("kontak")}
          />
        )}
      </main>

      {selectedItem && (
        <DetailBarangModal
          item={selectedItem}
          history={data.history}
          recipes={data.recipes}
          items={data.items}
          contacts={data.contacts}
          branches={data.branches || []}
          transactions={data.transactions || []}
          salespeople={data.salespeople || []}
          persons={data.persons || []}
          siblings={siblingsOf(data, selectedCode)}
          onClose={() => setSelectedCode(null)}
          onEdit={(it) => { setSelectedCode(null); setItemModal({ initial: it }); }}
          onAdjust={(it) => { setSelectedCode(null); setAdjustItem(it); }}
          onOpenItem={(code) => setSelectedCode(code)}
          onOpenTransformation={(id) => { setSelectedCode(null); setRiwayatFocusId(id); setPage("riwayat"); }}
        />
      )}

      {itemModal && (
        <ItemFormModal
          initial={itemModal.initial}
          suggestedCode={undefined}
          createIsSet={itemModal.isSet === true}
          recipes={data.recipes}
          categories={data.categories}
          cars={data.cars}
          items={data.items}
          contacts={data.contacts}
          initialSiblings={itemModal.initial ? siblingsOf(data, itemModal.initial.internalCode) : []}
          onSave={saveItem}
          onClose={() => setItemModal(null)}
          onAdjust={(it) => { setItemModal(null); setAdjustItem(it); }}
          onCreateCar={createCar}
          onCreateContact={createContact}
        />
      )}

      {adjustItem && (
        <PenyesuaianModal
          item={adjustItem}
          contacts={data.contacts}
          onCreateContact={createContact}
          onSubmit={applyAdjustment}
          onClose={() => setAdjustItem(null)}
        />
      )}

      {/* Rendered last so the confirm sits on top of any other open modal. */}
      {deleteTarget && (
        <DeleteConfirmModal
          title="Hapus barang?"
          message={<>
            Anda akan menghapus <b>{deleteTarget.name}</b> (<span className="mono">{deleteTarget.internalCode}</span>). Tindakan ini permanen.
            <div className="alert alert--info" style={{ marginTop: "var(--gap-md)" }}>
              Riwayat barang ini tetap tersimpan. Resep yang memakai barang ini akan menandainya "tidak tersedia".
            </div>
          </>}
          requireText="HAPUS"
          onConfirm={() => {
            deleteItem(deleteTarget.internalCode);
            if (selectedCode === deleteTarget.internalCode) setSelectedCode(null);
            setDeleteTarget(null);
          }}
          onClose={() => setDeleteTarget(null)}
        />
      )}

      {selectedRecipe && (
        <RecipeDetailModal
          recipe={selectedRecipe}
          items={data.items}
          onEdit={() => { setEditRecipeTarget(selectedRecipe); setSelectedRecipeId(null); }}
          onClose={() => setSelectedRecipeId(null)}
        />
      )}

      {deleteRecipeTarget && (
        <ConfirmModal
          title="Hapus resep?"
          message={<>Anda akan menghapus resep <b>{deleteRecipeTarget.name}</b>. Tindakan ini tidak memengaruhi stok.</>}
          confirmLabel="Hapus"
          requireText="HAPUS"
          onConfirm={() => {
            deleteRecipe(deleteRecipeTarget.id);
            if (selectedRecipeId === deleteRecipeTarget.id) setSelectedRecipeId(null);
            setDeleteRecipeTarget(null);
          }}
          onClose={() => setDeleteRecipeTarget(null)}
        />
      )}

      {selectedCar && (
        <MobilDetailModal
          car={selectedCar}
          onClose={() => setSelectedCarKey(null)}
          onOpenItem={(code) => { setSelectedCarKey(null); setSelectedCode(code); }}
        />
      )}

      {editRecipeTarget && (
        <RecipeEditModal
          recipe={editRecipeTarget}
          items={data.items}
          onSave={(patch) => { updateRecipe(editRecipeTarget.id, patch); setEditRecipeTarget(null); }}
          onClose={() => setEditRecipeTarget(null)}
        />
      )}

      {kelolaKategori && (
        <KelolaKategoriModal
          categories={data.categories}
          items={data.items}
          onCreateCategory={createCategory}
          onRenameCategory={handleRenameCategory}
          onDeleteCategory={handleDeleteCategory}
          onOpenCategory={(cat) => setSelectedCategory(cat)}
          onClose={() => setKelolaKategori(false)}
        />
      )}
      {selectedCategory && (
        <KategoriDetailModal
          category={selectedCategory}
          items={data.items}
          onOpenItem={(code) => { setSelectedCategory(null); setKelolaKategori(false); setSelectedCode(code); }}
          onClose={() => setSelectedCategory(null)}
        />
      )}
      {pengaturanOpen && (
        <PengaturanModal
          company={data.settings?.company}
          bankAccount={data.settings?.bankAccount}
          defaultDueDays={data.settings?.defaultDueDays ?? 14}
          branches={data.branches || []}
          salespeople={data.salespeople || []}
          transactions={data.transactions || []}
          onAddBranch={handleAddBranch}
          onUpdateBranch={handleUpdateBranch}
          onDeleteBranch={handleDeleteBranch}
          onSave={({ defaultDueDays, bankAccount, ...company }) => {
            updateCompanySettings(company);
            setDefaultDueDays(defaultDueDays);
            updatePusatBankAccount(bankAccount);
            setPengaturanOpen(false);
          }}
          onClose={() => setPengaturanOpen(false)}
          deviceLabel={saveQueue ? device.label : null}
          onSaveDeviceLabel={(label) => setDevice(saveDeviceLabel(label))}
        />
      )}
      {saveQueue && !device.asked && !devicePromptDismissed && !pengaturanOpen && (
        <DeviceLabelModal
          onSave={(label) => setDevice(saveDeviceLabel(label))}
          onSkip={() => setDevice(saveDeviceLabel(""))}
          onClose={() => setDevicePromptDismissed(true)}
        />
      )}
    </div>
    </ExpensesContext.Provider>
  );
}
